import React, { useState, useEffect } from 'react';
import { formatCurrencyWhole as formatCurrency } from '../lib/format';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTenantBranding } from '../hooks/useTenantBranding-runtime';
import {
  Users,
  ShoppingCart,
  DollarSign,
  TrendingUp,
  Package,
  Building2,
  Shield,
  UserCheck,
  Activity,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  User,
  Mail,
  Phone,
  CreditCard,
  Calendar,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { useUser } from '../store/useAuthStore';
import { apiClient } from '../lib/api';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminHeader,
  useAdminTrends,
  useAdminRankings,
} from '../hooks/useAdminDashboard';
import {
  useSupervisorHeader,
  useSupervisorTrends,
  useSupervisorRankings,
} from '../hooks/useSupervisorDashboard';
import {
  useDistributorHeader,
  useDistributorTrends,
  useDistributorRankings,
} from '../hooks/useDistributorDashboard';
import { LineChart, BarChart, PieChart } from '../components/charts';
import { RevenueBreakdownModal } from '../components/analytics/RevenueBreakdownModal';
import { PhasedMetricsPanel } from '../components/analytics/PhasedMetricsPanel';
import type { PhaseMetricConfig, PhaseValues } from '../components/analytics/PhasedMetricsPanel';
import { formatDayString, formatMonthString } from '../utils/dateUtils';
import { ReorderRankingTable } from '../components/analytics/ReorderRankingTable';
import { OrderCutoffNotification } from '../components/common/OrderCutoffNotification';
import VolumeRewardsWidget from '../components/physician/VolumeRewardsWidget';

// Copy for the physician verification banner, keyed off ID-proofing status so a
// clinic that has already submitted sees "pending review" rather than a prompt to
// go complete steps it has already done. `cta` null hides the action button.
function physicianVerificationBanner(
  idProofingStatus?: string,
  physicianData?: { telemedOnly?: boolean; idProofingData?: { agreementAccepted?: boolean } }
): { title: string; body: string; cta: string | null } {
  // A telemed-only clinic's second gate is its signed Contract + BAA, not a
  // license check. An admin can approve the ID step (force-verify, or finishing a
  // network assignment) before the clinic ever signs — which left these clinics on
  // "we'll email you" with no way forward. Send them to sign instead.
  if (
    idProofingStatus === 'approved' &&
    physicianData?.telemedOnly === true &&
    physicianData?.idProofingData?.agreementAccepted !== true
  ) {
    return {
      title: 'Sign your telemedicine agreement',
      body: 'Your clinic prescribes through the telemedicine network. Sign the Physician Services Agreement & BAA to unlock your dashboard, patients and ordering.',
      cta: 'Review & sign',
    };
  }
  switch (idProofingStatus) {
    case 'submitted':
      return {
        title: 'Verification Pending Review',
        body: "Thanks — your information has been submitted and is under review. We'll email you once your account is approved. Some features stay locked until then.",
        cta: 'View status',
      };
    case 'approved':
      // Gate A approved but not fully verified yet (e.g. license verification pending).
      return {
        title: 'Verification In Progress',
        body: "Your documents were approved and your license verification is being finalized. We'll email you once everything is complete.",
        cta: null,
      };
    case 'rejected':
      return {
        title: 'Verification Needs Attention',
        body: 'Some of your verification details need another look. Please review and resubmit to continue.',
        cta: 'Review & Resubmit',
      };
    default:
      return {
        title: 'Physician Verification Required',
        body: 'Complete your physician verification to access all features including analytics, patient management, and prescription ordering.',
        cta: 'Complete Verification',
      };
  }
}

// Shared interfaces for order breakdown
interface OrderBreakdown {
  patient: { total: number; thisMonth: number; revenue: number };
  bulk: { total: number; thisMonth: number; revenue: number };
}

interface BulkOrderStatusCounts {
  draft: number;
  pending_approval: number;
  awaiting_payment: number;
  paid: number;
  processing: number;
  shipped: number;
  delivered: number;
  voided: number;
  cancelled: number;
}

// Analytics interfaces
interface PhysicianAnalytics {
  totalPatients: number;
  newPatientsThisMonth: number;
  activePatients: number;
  totalOrders: number;
  monthlyOrders: number;
  ordersToday: number;
  ordersYesterday: number;
  revenueToday: number;
  revenueYesterday: number;
  revenueThisMonth: number;
  totalRevenue: number;
  averageOrderValue: number;
  ordersByStatus: {
    draft: number;
    awaiting_payment: number;
    submitted: number;
    processing: number;
    shipped: number;
    delivered: number;
    voided: number;
    cancelled: number;
    failed: number;
  };
  bulkOrdersByStatus: BulkOrderStatusCounts;
  orderBreakdown: OrderBreakdown;
  monthlyTrends: {
    month: string;
    orders: number;
    revenue: number;
    patients: number;
  }[];
  topProducts: {
    productName: string;
    orderCount: number;
    revenue: number;
  }[];
}

interface DistributorAnalytics {
  networkPhysicians: number;
  activePhysicians: number;
  totalPatients: number;
  totalOrders: number;
  monthlyOrders: number;
  ordersToday: number;
  ordersYesterday: number;
  revenueToday: number;
  revenueYesterday: number;
  networkRevenue: number;
  monthlyRevenue: number;
  averageOrderValue: number;
  revenueBreakdown: {
    // Legacy fields
    pharmacyRevenue: number;
    distributorRevenue: number;
    physicianRevenue: number;
    monthlyPharmacyRevenue: number;
    monthlyDistributorRevenue: number;
    monthlyPhysicianRevenue: number;
    // New explicit breakdown fields (paid/collected)
    // These are optional because supervisors get productCost instead
    pharmacyCost?: number;
    adminMargin?: number;
    distributorMargin: number;
    physicianMargin: number;
    totalRevenue: number;
    monthlyPharmacyCost?: number;
    monthlyAdminMargin?: number;
    monthlyDistributorMargin: number;
    monthlyPhysicianMargin: number;
    monthlyTotalRevenue: number;
    // Supervisor combined cost (replaces pharmacyCost + adminMargin)
    productCost?: number;
    monthlyProductCost?: number;
    placedProductCost?: number;
    // Placed revenue (orders created)
    placedPharmacyCost?: number;
    placedAdminMargin?: number;
    placedDistributorMargin?: number;
    placedPhysicianMargin?: number;
    placedTotalRevenue?: number;
    // Outstanding = placed - paid
    outstandingRevenue?: number;
  };
  topPhysicians: {
    physicianId: string;
    physicianName: string;
    orderCount: number;
    revenue: number;
    patientCount: number;
  }[];
  monthlyTrends: {
    month: string;
    orders: number;
    revenue: number;
    physicians: number;
  }[];
  ordersByStatus: {
    draft: number;
    awaiting_payment: number;
    submitted: number;
    processing: number;
    shipped: number;
    delivered: number;
    voided: number;
    cancelled: number;
    failed: number;
  };
  bulkOrdersByStatus: BulkOrderStatusCounts;
  orderBreakdown: OrderBreakdown;
}

// Piecewise slices — three subsets of the supervisor payload (which is
// shaped like DistributorAnalytics) that are fetched by independent
// endpoints. The combined shape above stays exact so other pages
// (Analytics.tsx) keep working.
type SupervisorAnalyticsHeader = Pick<DistributorAnalytics,
  | 'networkPhysicians'
  | 'activePhysicians'
  | 'totalPatients'
  | 'totalOrders'
  | 'monthlyOrders'
  | 'ordersToday'
  | 'ordersYesterday'
  | 'revenueToday'
  | 'revenueYesterday'
  | 'networkRevenue'
  | 'monthlyRevenue'
  | 'averageOrderValue'
  | 'ordersByStatus'
  | 'bulkOrdersByStatus'
  | 'orderBreakdown'
>;

type SupervisorAnalyticsTrends = Pick<DistributorAnalytics,
  | 'monthlyTrends'
  | 'revenueBreakdown'
>;

type SupervisorAnalyticsRankings = Pick<DistributorAnalytics,
  | 'topPhysicians'
>;

interface AdminAnalytics {
  totalUsers: number;
  activeUsers: number;
  pendingUsers: number;
  totalDistributors: number;
  totalPhysicians: number;
  totalPatients: number;
  totalOrders: number;
  monthlyOrders: number;
  ordersToday: number;
  ordersYesterday: number;
  revenueToday: number;
  revenueYesterday: number;
  totalRevenue: number;
  monthlyRevenue: number;
  averageOrderValue: number;
  revenueBreakdown: {
    // Legacy fields
    pharmacyRevenue: number;
    distributorRevenue: number;
    physicianRevenue: number;
    monthlyPharmacyRevenue: number;
    monthlyDistributorRevenue: number;
    monthlyPhysicianRevenue: number;
    // New explicit breakdown fields (paid/collected)
    pharmacyCost: number;
    adminMargin: number;
    supervisorMargin: number;
    distributorMargin: number;
    physicianMargin: number;
    totalRevenue: number;
    monthlyPharmacyCost: number;
    monthlyAdminMargin: number;
    monthlySupervisorMargin: number;
    monthlyDistributorMargin: number;
    monthlyPhysicianMargin: number;
    monthlyTotalRevenue: number;
    // Placed revenue (orders created)
    placedPharmacyCost?: number;
    placedAdminMargin?: number;
    placedSupervisorMargin?: number;
    placedDistributorMargin?: number;
    placedPhysicianMargin?: number;
    placedTotalRevenue?: number;
    // Outstanding = placed - paid
    outstandingRevenue?: number;
  };
  userGrowth: {
    month: string;
    users: number;
    physicians: number;
    distributors: number;
  }[];
  ordersByStatus: {
    draft: number;
    awaiting_payment: number;
    submitted: number;
    processing: number;
    shipped: number;
    delivered: number;
    voided: number;
    cancelled: number;
    failed: number;
  };
  bulkOrdersByStatus: BulkOrderStatusCounts;
  orderBreakdown: OrderBreakdown;
  revenueByMonth: {
    month: string;
    revenue: number;
    orders: number;
    // New dual-perspective fields
    revenuePlaced?: number;
    revenueCollected?: number;
    ordersPlaced?: number;
    ordersCollected?: number;
    expenses?: number;
  }[];
  topDistributors: {
    distributorId: string;
    distributorName: string;
    physicianCount: number;
    orderCount: number;
    revenue: number;
  }[];
  topPhysicians: {
    physicianId: string;
    physicianName: string;
    orderCount: number;
    revenue: number;
  }[];
}

// Piecewise slices — three subsets of AdminAnalytics that are fetched by
// independent endpoints. The combined shape above stays exact so other
// pages (Analytics.tsx) keep working.
type AdminAnalyticsHeader = Pick<AdminAnalytics,
  | 'totalUsers'
  | 'activeUsers'
  | 'pendingUsers'
  | 'totalDistributors'
  | 'totalPhysicians'
  | 'totalPatients'
  | 'totalOrders'
  | 'monthlyOrders'
  | 'ordersToday'
  | 'ordersYesterday'
  | 'revenueToday'
  | 'revenueYesterday'
  | 'totalRevenue'
  | 'monthlyRevenue'
  | 'averageOrderValue'
  | 'ordersByStatus'
  | 'bulkOrdersByStatus'
  | 'orderBreakdown'
>;

type AdminAnalyticsTrends = Pick<AdminAnalytics,
  | 'userGrowth'
  | 'revenueByMonth'
  | 'revenueBreakdown'
>;

type AdminAnalyticsRankings = Pick<AdminAnalytics,
  | 'topDistributors'
  | 'topPhysicians'
>;

interface PhysicianPhasedAnalytics {
  scope: 'physician';
  summary: {
    totalRevenue: PhaseValues;
    thirtyDayRevenue?: PhaseValues;
    physicianMargin: PhaseValues;
    clinicCost: PhaseValues;
  };
}

interface DistributorPhasedAnalytics {
  scope: 'distributor';
  summary: {
    totalRevenue: PhaseValues;
    thirtyDayRevenue?: PhaseValues;
    // AM's own cost of goods (includes admin + supervisor margins so neither
    // is inferable from this response).
    productCost: PhaseValues;
    distributorMargin: PhaseValues;
    physicianMargin: PhaseValues;
  };
}

interface SupervisorPhasedAnalytics {
  scope: 'supervisor';
  summary: {
    totalRevenue: PhaseValues;
    thirtyDayRevenue?: PhaseValues;
    productCost: PhaseValues;
    supervisorMargin: PhaseValues;
    distributorMargin: PhaseValues;
    physicianMargin: PhaseValues;
  };
}

interface AdminPhasedAnalytics {
  scope: 'admin';
  summary: {
    totalRevenue: PhaseValues;
    thirtyDayRevenue?: PhaseValues;
    pharmacyCost: PhaseValues;
    adminMargin: PhaseValues;
    supervisorMargin: PhaseValues;
    distributorMargin: PhaseValues;
    physicianMargin: PhaseValues;
  };
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  color: string;
  bgGradient: string;
  isLoading?: boolean;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon: Icon, trend, color: _color, bgGradient, isLoading, onClick }) => (
  <div 
    className={`bg-white overflow-hidden shadow-sm rounded-2xl border border-gray-200 hover:shadow-md transition-shadow duration-200 ${
      onClick ? 'cursor-pointer hover:border-blue-300' : ''
    }`}
    onClick={onClick}
  >
    <div className="p-4 lg:p-6">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-xl ${bgGradient} flex items-center justify-center shadow-sm`}>
            {isLoading ? (
              <RefreshCw className="h-5 w-5 lg:h-6 lg:w-6 text-white animate-spin" />
            ) : (
              <Icon className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
            )}
          </div>
        </div>
        <div className="ml-4 lg:ml-5 w-0 flex-1">
          <dl>
            <dt className="text-xs lg:text-sm font-medium text-gray-500 truncate">{title}</dt>
            <dd className="flex items-baseline mt-1">
              <div className="text-xl lg:text-2xl font-bold text-gray-900">
                {isLoading ? (
                  <div className="h-6 lg:h-8 w-12 lg:w-16 bg-gray-200 animate-pulse rounded"></div>
                ) : (
                  value
                )}
              </div>
              {trend && !isLoading && (
                <div className={`ml-2 lg:ml-3 flex items-baseline text-xs lg:text-sm font-semibold ${
                  trend.isPositive ? 'text-green-600' : 'text-red-600'
                }`}>
                  <TrendingUp className={`self-center flex-shrink-0 h-3 w-3 lg:h-4 lg:w-4 ${
                    trend.isPositive ? 'text-green-500' : 'text-red-500 rotate-180'
                  }`} />
                  <span className="ml-1">
                    {Math.abs(trend.value)}%
                  </span>
                </div>
              )}
            </dd>
          </dl>
        </div>
      </div>
    </div>
  </div>
);

// Orders Today Section - bigger component with comparison
interface OrdersTodaySectionProps {
  ordersToday: number;
  ordersYesterday: number;
  revenueToday: number;
  revenueYesterday: number;
  // For physician dashboards where the amount represents the physician's
  // cost of goods rather than revenue.
  revenueLabel?: string;
  isLoading?: boolean;
  onClick?: () => void;
}

const OrdersTodaySection: React.FC<OrdersTodaySectionProps> = ({
  ordersToday,
  ordersYesterday,
  revenueToday,
  revenueYesterday,
  revenueLabel = 'Revenue',
  isLoading,
  onClick
}) => {
  const ordersDiff = ordersToday - ordersYesterday;
  const ordersUp = ordersDiff >= 0;

  const revenueDiff = revenueToday - revenueYesterday;
  const revenuePercentChange = revenueYesterday > 0
    ? Math.round((revenueDiff / revenueYesterday) * 100)
    : revenueToday > 0 ? 100 : 0;
  const revenueUp = revenueDiff >= 0;

  return (
    <div
      className={`bg-gradient-to-br from-indigo-500 to-purple-600 overflow-hidden shadow-lg rounded-2xl border border-indigo-400 hover:shadow-xl transition-all duration-200 ${
        onClick ? 'cursor-pointer' : ''
      }`}
      onClick={onClick}
    >
      <div className="p-5 lg:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Today's Activity</h3>
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <Calendar className="h-5 w-5 text-white" />
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <div className="h-12 bg-white/20 animate-pulse rounded-lg"></div>
            <div className="h-8 bg-white/20 animate-pulse rounded-lg"></div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {/* Orders */}
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-indigo-100 text-sm font-medium mb-1">Orders</p>
              <p className="text-3xl font-bold text-white">{ordersToday.toLocaleString()}</p>
              <div className="flex items-center mt-2">
                {ordersUp ? (
                  <ArrowUpRight className="h-4 w-4 text-green-300 mr-1" />
                ) : (
                  <ArrowDownRight className="h-4 w-4 text-red-300 mr-1" />
                )}
                <span className={`text-sm font-medium ${ordersUp ? 'text-green-300' : 'text-red-300'}`}>
                  {Math.abs(ordersDiff)} {ordersUp ? 'more' : 'fewer'}
                </span>
                <span className="text-indigo-200 text-sm ml-1">vs yesterday</span>
              </div>
            </div>

            {/* Revenue */}
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-indigo-100 text-sm font-medium mb-1">{revenueLabel}</p>
              <p className="text-3xl font-bold text-white">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(revenueToday)}
              </p>
              <div className="flex items-center mt-2">
                {revenueUp ? (
                  <ArrowUpRight className="h-4 w-4 text-green-300 mr-1" />
                ) : (
                  <ArrowDownRight className="h-4 w-4 text-red-300 mr-1" />
                )}
                <span className={`text-sm font-medium ${revenueUp ? 'text-green-300' : 'text-red-300'}`}>
                  {revenuePercentChange}%
                </span>
                <span className="text-indigo-200 text-sm ml-1">vs yesterday</span>
              </div>
            </div>
          </div>
        )}

        {/* Yesterday comparison bar */}
        <div className="mt-4 pt-4 border-t border-white/20">
          <div className="flex justify-between text-sm">
            <span className="text-indigo-200">Yesterday</span>
            <span className="text-white font-medium">
              {ordersYesterday} orders • {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(revenueYesterday)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Date range presets
type DateRangePreset = '7d' | '30d' | '90d' | 'ytd' | 'all' | 'custom';

// Custom date range for when 'custom' preset is selected
interface CustomDateRange {
  startDate: Date;
  endDate: Date;
}

// Trend interval type for charts
type TrendInterval = 'daily' | 'weekly' | 'monthly';

interface TrendIntervalSelectorProps {
  selectedInterval: TrendInterval;
  onIntervalChange: (interval: TrendInterval) => void;
  className?: string;
}

const TrendIntervalSelector: React.FC<TrendIntervalSelectorProps> = ({
  selectedInterval,
  onIntervalChange,
  className = ''
}) => {
  const intervals: { value: TrendInterval; label: string }[] = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
  ];

  return (
    <select
      value={selectedInterval}
      onChange={(e) => onIntervalChange(e.target.value as TrendInterval)}
      className={`text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
    >
      {intervals.map((interval) => (
        <option key={interval.value} value={interval.value}>
          {interval.label}
        </option>
      ))}
    </select>
  );
};

interface DateRangeSelectorProps {
  selectedPreset: DateRangePreset;
  onPresetChange: (preset: DateRangePreset) => void;
  customDateRange?: CustomDateRange;
  onCustomDateRangeChange?: (range: CustomDateRange) => void;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  selectedPreset,
  onPresetChange,
  customDateRange,
  onCustomDateRangeChange
}) => {
  const [isCustomPickerOpen, setIsCustomPickerOpen] = useState(false);

  const presets: { value: DateRangePreset; label: string }[] = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
    { value: 'ytd', label: 'YTD' },
    { value: 'all', label: 'All' },
  ];

  const formatDateTimeShort = (date: Date) => {
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const toDatetimeLocalValue = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${d}T${h}:${min}`;
  };

  const handleCustomClick = () => {
    if (selectedPreset === 'custom') {
      setIsCustomPickerOpen(!isCustomPickerOpen);
    } else {
      onPresetChange('custom');
      setIsCustomPickerOpen(true);
    }
  };

  const handleCustomDateChange = (field: 'startDate' | 'endDate', value: string) => {
    if (!onCustomDateRangeChange || !customDateRange) return;
    // datetime-local gives "YYYY-MM-DDTHH:MM" in local time
    const newDate = new Date(value);
    if (!isNaN(newDate.getTime())) {
      onCustomDateRangeChange({
        ...customDateRange,
        [field]: newDate
      });
    }
  };

  const handleApplyCustomRange = () => {
    setIsCustomPickerOpen(false);
  };

  return (
    <div className="flex items-center gap-2 relative">
      <Calendar className="h-4 w-4 text-gray-500" />
      <div className="flex rounded-lg border border-gray-200 bg-white p-0.5">
        {presets.map((preset) => (
          <button
            key={preset.value}
            onClick={() => {
              onPresetChange(preset.value);
              setIsCustomPickerOpen(false);
            }}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              selectedPreset === preset.value
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {preset.label}
          </button>
        ))}
        <button
          onClick={handleCustomClick}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            selectedPreset === 'custom'
              ? 'bg-blue-600 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          {selectedPreset === 'custom' && customDateRange
            ? `${formatDateTimeShort(customDateRange.startDate)} - ${formatDateTimeShort(customDateRange.endDate)}`
            : 'Custom'}
        </button>
      </div>

      {/* Custom Date Picker Dropdown */}
      {isCustomPickerOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsCustomPickerOpen(false)}
          />
          <div className="absolute top-full right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Select Date & Time Range</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start Date & Time</label>
                <input
                  type="datetime-local"
                  value={customDateRange ? toDatetimeLocalValue(customDateRange.startDate) : ''}
                  onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                  max={customDateRange ? toDatetimeLocalValue(customDateRange.endDate) : undefined}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">End Date & Time</label>
                <input
                  type="datetime-local"
                  value={customDateRange ? toDatetimeLocalValue(customDateRange.endDate) : ''}
                  onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                  min={customDateRange ? toDatetimeLocalValue(customDateRange.startDate) : undefined}
                  max={toDatetimeLocalValue(new Date())}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end mt-4 pt-3 border-t border-gray-200">
              <button
                onClick={handleApplyCustomRange}
                className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Helper to convert preset to date range
const getDateRangeFromPreset = (
  preset: DateRangePreset,
  customRange?: CustomDateRange
): { startDate: Date; endDate: Date } | undefined => {
  if (preset === 'custom' && customRange) {
    return { startDate: customRange.startDate, endDate: customRange.endDate };
  }

  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  const startDate = new Date();

  switch (preset) {
    case '7d':
      startDate.setDate(startDate.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate };
    case '30d':
      startDate.setDate(startDate.getDate() - 30);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate };
    case '90d':
      startDate.setDate(startDate.getDate() - 90);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate };
    case 'ytd':
      startDate.setMonth(0, 1);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate };
    case 'all':
      return undefined; // No date filter
    default:
      startDate.setDate(startDate.getDate() - 30);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate };
  }
};

// Default custom date range (last 30 days)
const getDefaultCustomDateRange = (): CustomDateRange => {
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);
  startDate.setHours(0, 0, 0, 0);
  return { startDate, endDate };
};

const mergeThirtyDayRevenue = <T extends { summary: Record<string, PhaseValues | undefined> }>(
  phasedAnalytics: T,
  rollingThirtyDayRevenue?: PhaseValues
): T => ({
  ...phasedAnalytics,
  summary: {
    ...phasedAnalytics.summary,
    thirtyDayRevenue: rollingThirtyDayRevenue ?? phasedAnalytics.summary.totalRevenue
  }
});

// Utility function to format currency

// Utility function to get status colors
const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    draft: '#94a3b8',
    pending: '#f59e0b',
    submitted: '#3b82f6',
    processing: '#8b5cf6',
    shipped: '#10b981',
    delivered: '#059669',
    cancelled: '#ef4444',
    failed: '#dc2626'
  };
  return colors[status] || '#6b7280';
};

const physicianPhaseMetrics: PhaseMetricConfig[] = [
  {
    key: 'totalRevenue',
    label: 'Total Cost',
    description: 'Paid, invoiced, and fully placed order cost.',
    accentClass: 'bg-yellow-500',
    valueClass: 'text-gray-900',
    hoverBorderClass: 'hover:bg-yellow-100'
  }
];

// Extra phased metrics shown only when the physician's clinic applies markup.
const physicianClinicMarkupMetrics: PhaseMetricConfig[] = [
  {
    key: 'clinicCost',
    label: 'Upstream Cost',
    description: 'Paid to pharmacy + upstream tiers.',
    accentClass: 'bg-gray-500',
    valueClass: 'text-gray-900',
    hoverBorderClass: 'hover:bg-gray-100'
  },
  {
    key: 'physicianMargin',
    label: 'Clinic Margin',
    description: "Your clinic's markup retained.",
    accentClass: 'bg-green-500',
    valueClass: 'text-green-900',
    hoverBorderClass: 'hover:bg-green-100'
  }
];

const physicianHasClinicMarkup = (phased: PhysicianPhasedAnalytics | null): boolean => {
  if (!phased?.summary.physicianMargin) return false;
  const m = phased.summary.physicianMargin;
  return (m.realized || 0) + (m.booked || 0) + (m.pipeline || 0) > 0;
};


const distributorPhaseMetrics: PhaseMetricConfig[] = [
  {
    key: 'productCost',
    label: 'Your Cost',
    description: 'Your cost of goods on these orders',
    accentClass: 'bg-gray-500',
    titleClass: 'text-gray-900',
    valueClass: 'text-gray-900',
    descriptionClass: 'text-gray-600',
    hoverBorderClass: 'hover:border-gray-400'
  },
  {
    key: 'distributorMargin',
    label: 'Your Account Manager Margin',
    description: 'Your commission on orders',
    accentClass: 'bg-red-500',
    titleClass: 'text-red-900',
    valueClass: 'text-red-900',
    descriptionClass: 'text-red-600',
    hoverBorderClass: 'hover:border-red-300'
  },
  {
    key: 'totalRevenue',
    label: 'Total Network Revenue',
    description: 'What physicians paid for orders',
    accentClass: 'bg-green-500',
    titleClass: 'text-green-900',
    valueClass: 'text-green-900',
    descriptionClass: 'text-green-600',
    hoverBorderClass: 'hover:border-green-300'
  }
];
const supervisorPhaseMetrics: PhaseMetricConfig[] = [
  {
    key: 'productCost',
    label: 'Product Cost',
    description: 'Base cost before AM margins',
    accentClass: 'bg-gray-500',
    titleClass: 'text-gray-900',
    valueClass: 'text-gray-900',
    descriptionClass: 'text-gray-600',
    hoverBorderClass: 'hover:border-gray-400'
  },
  {
    key: 'supervisorMargin',
    label: 'Your Commission',
    description: 'Your commission on network orders',
    accentClass: 'bg-orange-500',
    titleClass: 'text-orange-900',
    valueClass: 'text-orange-900',
    descriptionClass: 'text-orange-600',
    hoverBorderClass: 'hover:border-orange-300'
  },
  {
    key: 'distributorMargin',
    label: 'Account Manager Margins',
    description: 'Commission earned by AMs in your network.',
    accentClass: 'bg-red-500',
    titleClass: 'text-red-900',
    valueClass: 'text-red-900',
    descriptionClass: 'text-red-600',
    hoverBorderClass: 'hover:border-red-300'
  },
  {
    key: 'totalRevenue',
    label: 'Total Network Revenue',
    description: 'What physicians paid for orders',
    accentClass: 'bg-green-500',
    titleClass: 'text-green-900',
    valueClass: 'text-green-900',
    descriptionClass: 'text-green-600',
    hoverBorderClass: 'hover:border-green-300'
  }
];

const adminPhaseMetrics: PhaseMetricConfig[] = [
  {
    key: 'pharmacyCost',
    label: 'Pharmacy Cost',
    accentClass: 'text-gray-600',
    icon: Package,
    iconBgClass: 'bg-gray-100',
    valueClass: 'text-gray-700',
    hoverBorderClass: 'hover:border-gray-400'
  },
  {
    key: 'adminMargin',
    label: 'Admin Margin',
    accentClass: 'text-green-600',
    icon: DollarSign,
    iconBgClass: 'bg-green-100',
    valueClass: 'text-green-600',
    hoverBorderClass: 'hover:border-green-300'
  },
  {
    key: 'supervisorMargin',
    label: 'Supervisor Margin',
    accentClass: 'text-orange-600',
    icon: Shield,
    iconBgClass: 'bg-orange-100',
    valueClass: 'text-orange-600',
    hoverBorderClass: 'hover:border-orange-300'
  },
  {
    key: 'distributorMargin',
    label: 'Account Manager Margin',
    accentClass: 'text-red-600',
    icon: Building2,
    iconBgClass: 'bg-red-100',
    valueClass: 'text-red-600',
    hoverBorderClass: 'hover:border-red-300'
  },
  {
    key: 'totalRevenue',
    label: 'Total Revenue',
    accentClass: 'text-blue-600',
    icon: TrendingUp,
    iconBgClass: 'bg-blue-100',
    valueClass: 'text-blue-600',
    hoverBorderClass: 'hover:border-blue-300'
  }
];

// Utility function to calculate percentage change
const calculateTrend = (current: number, previous: number): { value: number; isPositive: boolean } | undefined => {
  if (previous === 0) return undefined;
  const change = ((current - previous) / previous) * 100;
  return {
    value: Math.round(Math.abs(change)),
    isPositive: change >= 0
  };
};

const PhysicianDashboard: React.FC = () => {
  const user = useUser();
  const navigate = useNavigate();
  const { branding } = useTenantBranding();
  const [analytics, setAnalytics] = useState<PhysicianAnalytics | null>(null);
  const [phasedAnalytics, setPhasedAnalytics] = useState<PhysicianPhasedAnalytics | null>(null);
  const [distributor, setDistributor] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('30d');
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange>(getDefaultCustomDateRange());
  const [revenueInterval, setRevenueInterval] = useState<TrendInterval>('monthly');
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);

  // Get date range from preset (default 30 days)
  const getDateRange = () => getDateRangeFromPreset(dateRangePreset, customDateRange);

  const handleDateRangeChange = (preset: DateRangePreset) => {
    setDateRangePreset(preset);
  };

  const fetchAnalytics = async () => {
    try {
      setError(null);
      const userId = user?._id;
      const [response, phasedResponse, rollingThirtyDayPhasedResponse] = await Promise.all([
        apiClient.getPhysicianAnalytics(undefined, getDateRange(), { revenueInterval }),
        userId
          ? apiClient.getPhysicianPhasedAnalytics(userId, getDateRange())
          : Promise.resolve({ success: false, error: 'Missing physician id' } as any),
        userId
          ? apiClient.getPhysicianPhasedAnalytics(userId, getDateRangeFromPreset('30d', customDateRange))
          : Promise.resolve({ success: false, error: 'Missing physician id' } as any)
      ]);

      if (response.success && response.data) {
        setAnalytics(response.data);
        if (phasedResponse.success && phasedResponse.data) {
          setPhasedAnalytics(
            mergeThirtyDayRevenue(
              phasedResponse.data,
              rollingThirtyDayPhasedResponse.success && rollingThirtyDayPhasedResponse.data
                ? rollingThirtyDayPhasedResponse.data.summary.totalRevenue
                : undefined
            )
          );
        } else {
          setPhasedAnalytics(null);
        }
      } else {
        // Check if this is a verification error
        if (response.error?.includes('verification required') || response.error?.includes('Physician verification')) {
          setError('Please complete your physician verification to access analytics and other features.');
        } else {
          setError(response.error || 'Failed to load analytics');
        }
      }
    } catch (err: any) {
      // Handle 403 verification errors gracefully
      if (err.response?.status === 403) {
        setError('Please complete your physician verification to access analytics and other features.');
      } else {
        setError(err.message || 'Failed to load analytics');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchDistributorInfo = async () => {
    try {
      if (user && user.physicianData?.distributorId) {
        // Handle both string and object distributorId
        const distributorId = typeof user.physicianData.distributorId === 'object'
          ? (user.physicianData.distributorId as any)?._id || (user.physicianData.distributorId as any)?.toString()
          : user.physicianData.distributorId;

        const response = await apiClient.get(`/users/${distributorId}/profile`);
        if (response.success && response.data) {
          setDistributor(response.data);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch distributor info:', err);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const response = await apiClient.getBulkOrders({ status: 'pending_approval' });
      if (response.success && response.data) {
        // Filter to only show orders for this physician
        const myPendingApprovals = response.data.filter(
          (order: any) => order.physicianId?._id === user?._id || order.physicianId === user?._id
        );
        // The list is one page (100). For a physician the server already scopes
        // to their own orders, so its total is the true count past that page.
        const total = response.pagination?.total;
        const scopedToMe = user?.role === 'physician' || user?.role === 'physician_readonly';
        setPendingApprovalsCount(scopedToMe && typeof total === 'number' ? total : myPendingApprovals.length);
      }
    } catch (err: any) {
      console.error('Failed to fetch pending approvals:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRangePreset, customDateRange, revenueInterval]);

  useEffect(() => {
    fetchDistributorInfo();
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchPendingApprovals();
    }
  }, [user]);

  if (loading) {
    return (
      <div className="space-y-6 lg:space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {[1, 2, 3, 4].map((i) => (
      <StatCard
              key={i}
              title="Loading..."
              value=""
              icon={RefreshCw}
              color="bg-gray-500"
              bgGradient="bg-gradient-to-br from-gray-500 to-gray-600"
              isLoading={true}
            />
          ))}
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
          <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
            <div className="animate-pulse">
              <div className="h-6 w-32 bg-gray-200 rounded mb-4 lg:mb-6"></div>
              <div className="h-48 lg:h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
          <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
            <div className="animate-pulse">
              <div className="h-6 w-32 bg-gray-200 rounded mb-4 lg:mb-6"></div>
              <div className="h-48 lg:h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !error.includes('verification')) {
    return (
      <div className="text-center py-8 lg:py-12">
        <XCircle className="w-12 h-12 lg:w-16 lg:h-16 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg lg:text-xl font-medium text-gray-900 mb-2">Failed to load analytics</h3>
        <p className="text-sm lg:text-base text-gray-600 mb-4">{error}</p>
        <button
          onClick={fetchAnalytics}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  if (!analytics) {
    // If we have a verification error, show the dashboard with verification banner
    if (error && error.includes('verification')) {
      return (
        <div className="space-y-6 lg:space-y-8">
          {/* Verification Status Banner */}
          {(() => {
            const vb = physicianVerificationBanner(user?.physicianData?.idProofingStatus, user?.physicianData as any);
            return (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 lg:p-6">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-yellow-800 mb-1">{vb.title}</h3>
                    <p className="text-sm text-yellow-700 mb-3">{vb.body}</p>
                    {vb.cta && (
                      <button
                        onClick={() => window.location.href = '/id-proofing'}
                        className="inline-flex items-center px-3 py-2 border border-yellow-300 shadow-sm text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                      >
                        {vb.cta}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Welcome Message */}
          <div className="text-center py-8 lg:py-12">
            <h2 className="text-xl lg:text-2xl font-semibold text-gray-900 mb-4">
              Welcome to {branding.companyName}
            </h2>
            <p className="text-gray-600">
              {user?.physicianData?.idProofingStatus === 'submitted'
                ? 'Your verification is under review — dashboard features will unlock once your account is approved.'
                : 'Please complete your physician verification to access your dashboard features.'}
            </p>
          </div>
        </div>
      );
    }
    
    return <div className="text-center py-8 lg:py-12 text-gray-500">No analytics data available</div>;
  }

  // Calculate trends (using simple month-over-month comparison if data is available)
  const previousMonthTrends = analytics.monthlyTrends[analytics.monthlyTrends.length - 2];

  const patientTrend = previousMonthTrends 
    ? calculateTrend(analytics.newPatientsThisMonth, previousMonthTrends.patients)
    : undefined;
  const orderTrend = previousMonthTrends 
    ? calculateTrend(analytics.monthlyOrders, previousMonthTrends.orders)
    : undefined;
  const revenueTrend = previousMonthTrends 
    ? calculateTrend(analytics.revenueThisMonth, previousMonthTrends.revenue)
    : undefined;

  // Prepare chart data
  const orderStatusData = Object.entries(analytics.ordersByStatus)
    .filter(([_, count]) => count > 0)
    .map(([status, count]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: count
    }));

  const physicianMetricsForRender = physicianHasClinicMarkup(phasedAnalytics)
    ? [...physicianPhaseMetrics, ...physicianClinicMarkupMetrics]
    : physicianPhaseMetrics;

  // Physicians don't get a revenue drill-down — cards stay informational only.
  const phasedMetricConfigs = physicianMetricsForRender;

  return (
    <div className="space-y-6 lg:space-y-8">
      <VolumeRewardsWidget />

      {/* Verification Status Banner */}
      {(user?.role === 'physician' && user?.isVerified === false) && (() => {
        const vb = physicianVerificationBanner(user?.physicianData?.idProofingStatus, user?.physicianData as any);
        return (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 lg:p-6">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-yellow-800 mb-1">{vb.title}</h3>
                <p className="text-sm text-yellow-700 mb-3">{vb.body}</p>
                {vb.cta && (
                  <button
                    onClick={() => window.location.href = '/id-proofing'}
                    className="inline-flex items-center px-3 py-2 border border-yellow-300 shadow-sm text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                  >
                    {vb.cta}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Unsigned Contract + BAA banner.
          Deliberately separate from the verification banner above, which only
          renders when isVerified === false. A clinic whose acceptance was
          recorded by an admin during the migration IS verified and unblocked,
          but we still hold no signature — so it needs its own prompt. */}
      {(() => {
        if (user?.role !== 'physician') return null;
        const pd: any = user?.physicianData || {};
        const usesNetwork = pd.telemedOnly === true || pd.useNetworkForUncoveredStates === true;
        if (!usesNetwork) return null;
        const idp: any = pd.idProofingData || {};
        const hasRealSignature = idp.agreementAccepted === true && !!idp.signatureFile;
        if (hasRealSignature) return null;
        return (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 lg:p-6">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-amber-800 mb-1">
                  Signature needed on your telemedicine agreement
                </h3>
                <p className="text-sm text-amber-700 mb-3">
                  Your clinic prescribes through the telemedicine network, which requires a signed
                  Physician Services Agreement &amp; BAA. We don't currently hold your signature.
                  Nothing is blocked — you can keep ordering while you get to this.
                </p>
                <button
                  onClick={() => window.location.href = '/settings?tab=contracts'}
                  className="inline-flex items-center px-3 py-2 border border-amber-300 shadow-sm text-sm leading-4 font-medium rounded-md text-amber-800 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                >
                  Review &amp; sign
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Pending Bulk Order Approvals Banner */}
      {pendingApprovalsCount > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 lg:p-6">
          <div className="flex items-start">
            <Clock className="h-5 w-5 text-orange-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-orange-800 mb-1">
                {pendingApprovalsCount} Bulk Order{pendingApprovalsCount > 1 ? 's' : ''} Awaiting Your Approval
              </h3>
              <p className="text-sm text-orange-700 mb-3">
                Your account manager has created bulk orders that need your review and approval before they can be processed.
              </p>
              <button
                onClick={() => navigate('/bulk-orders/list?status=pending_approval')}
                className="inline-flex items-center px-3 py-2 border border-orange-300 shadow-sm text-sm leading-4 font-medium rounded-md text-orange-700 bg-orange-100 hover:bg-orange-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
              >
                <Clock className="h-4 w-4 mr-2" />
                Review Orders
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Cutoff Notification */}
      <OrderCutoffNotification variant="banner" />

      {/* Header with date range selector and refresh button */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Patient Analytics</h1>
            <p className="text-sm lg:text-base text-gray-600">Overview of your practice performance</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm lg:text-base"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
        <div className="flex justify-start sm:justify-end">
          <DateRangeSelector
            selectedPreset={dateRangePreset}
            onPresetChange={handleDateRangeChange}
            customDateRange={customDateRange}
            onCustomDateRangeChange={setCustomDateRange}
          />
        </div>
      </div>

      {/* Account Manager Info */}
      {distributor && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-4 lg:p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Your Account Manager</h3>
                <p className="text-gray-600 text-sm mb-2">
                  {distributor.distributorData?.companyName || `${distributor.profile.firstName} ${distributor.profile.lastName}`}
                </p>
                <div className="space-y-1">
                  <div className="flex items-center text-sm text-gray-600">
                    <User className="h-4 w-4 mr-2" />
                    <span>{distributor.profile.firstName} {distributor.profile.lastName}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Mail className="h-4 w-4 mr-2" />
                    <a href={`mailto:${distributor.email}`} className="text-blue-600 hover:text-blue-800">
                      {distributor.email}
                    </a>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="h-4 w-4 mr-2" />
                    <a href={`tel:${distributor.profile.phone}`} className="text-blue-600 hover:text-blue-800">
                      {distributor.profile.phone}
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                Distributor
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Orders Today Section */}
      <OrdersTodaySection
        ordersToday={analytics.ordersToday}
        ordersYesterday={analytics.ordersYesterday}
        revenueToday={analytics.revenueToday}
        revenueYesterday={analytics.revenueYesterday}
        revenueLabel="Cost"
        onClick={() => navigate('/orders')}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <StatCard
          title="Total Patients"
          value={analytics.totalPatients.toLocaleString()}
          icon={Users}
          trend={patientTrend}
          color="bg-blue-500"
          bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          title="New This Month"
          value={analytics.newPatientsThisMonth.toLocaleString()}
          icon={UserCheck}
          color="bg-green-500"
          bgGradient="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          title="Monthly Orders"
          value={analytics.monthlyOrders.toLocaleString()}
          icon={ShoppingCart}
          trend={orderTrend}
          color="bg-indigo-500"
          bgGradient="bg-gradient-to-br from-indigo-500 to-indigo-600"
        />
        <StatCard
          title="Monthly Cost"
          value={formatCurrency(analytics.revenueThisMonth)}
          icon={DollarSign}
          trend={revenueTrend}
          color="bg-yellow-500"
          bgGradient="bg-gradient-to-br from-yellow-500 to-yellow-600"
        />
      </div>

      {phasedAnalytics && (
        <PhasedMetricsPanel
          title="Phased Cost View"
          subtitle="New realized, booked, and pipeline totals rendered alongside the legacy dashboard."
          metrics={phasedMetricConfigs}
          summary={phasedAnalytics.summary}
          variant="summary-list"
        />
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
        {/* Trends Chart */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <h3 className="text-base lg:text-lg font-semibold text-gray-900">Practice Trends</h3>
            <TrendIntervalSelector
              selectedInterval={revenueInterval}
              onIntervalChange={setRevenueInterval}
            />
          </div>
                     <LineChart
             data={analytics.monthlyTrends}
             xKey="month"
             lines={[
               { dataKey: 'orders', color: '#3B82F6', name: 'Orders' },
               { dataKey: 'revenue', color: '#10B981', name: 'Revenue ($)' },
               { dataKey: 'patients', color: '#8B5CF6', name: 'New Patients' }
             ]}
             height={250}
           />
        </div>

        {/* Order Status Distribution */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
          <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4 lg:mb-6">Order Status Distribution</h3>
                     <PieChart
             data={orderStatusData}
             height={250}
             colors={[
               '#3B82F6', // pending - blue
               '#10B981', // submitted - green
               '#F59E0B', // processing - yellow
               '#8B5CF6', // shipped - purple
               '#06B6D4', // delivered - cyan
               '#6B7280', // draft - gray
               '#EF4444', // cancelled - red
               '#DC2626'  // failed - dark red
             ]}
             showLegend={true}
           />
        </div>

        {/* Top Products */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
          <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4 lg:mb-6">Top Products by Orders</h3>
                     <BarChart
             data={analytics.topProducts}
             xKey="productName"
             bars={[
               { dataKey: 'orderCount', color: '#3B82F6', name: 'Orders' }
             ]}
             height={250}
           />
        </div>

        {/* Quick Stats */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
          <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4 lg:mb-6">Practice Summary</h3>
        <div className="space-y-4">
            <div className="flex justify-between items-center p-3 lg:p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-sm lg:text-base font-medium text-gray-700">Active Patients</span>
            </div>
              <span className="text-sm lg:text-base font-semibold text-gray-900">{analytics.activePatients.toLocaleString()}</span>
            </div>
            <div className="p-3 lg:p-4 bg-gray-50 rounded-xl">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm lg:text-base font-medium text-gray-700">Total Orders</span>
                </div>
                <span className="text-sm lg:text-base font-semibold text-gray-900">{analytics.totalOrders.toLocaleString()}</span>
              </div>
              {analytics.orderBreakdown && (
                <div className="mt-2 pl-6 space-y-1 text-xs text-gray-500">
                  <div className="flex justify-between">
                    <span>Patient Orders</span>
                    <span>{analytics.orderBreakdown.patient.total.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Bulk Orders</span>
                    <span>{analytics.orderBreakdown.bulk.total.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-between items-center p-3 lg:p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-sm lg:text-base font-medium text-gray-700">Total Revenue</span>
              </div>
              <span className="text-sm lg:text-base font-semibold text-gray-900">{formatCurrency(analytics.totalRevenue)}</span>
            </div>
            <div className="flex justify-between items-center p-3 lg:p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span className="text-sm lg:text-base font-medium text-gray-700">Avg Order Value</span>
            </div>
              <span className="text-sm lg:text-base font-semibold text-gray-900">{formatCurrency(analytics.averageOrderValue)}</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

// Piecewise distributor (AM) dashboard. Renders header/trends/rankings
// each from its own slice query so a slow getTopPhysicians lookup doesn't
// block the KPI strip. Only used when the user's role is 'distributor';
// supervisors keep the legacy combined-fetch path in DistributorDashboard
// below until the supervisor refactor lands.
const DistributorOwnDashboard: React.FC = () => {
  const user = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [phasedAnalytics, setPhasedAnalytics] = useState<DistributorPhasedAnalytics | null>(null);
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('30d');
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange>(getDefaultCustomDateRange());
  const [revenueInterval, setRevenueInterval] = useState<TrendInterval>('monthly');
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Revenue breakdown modal state
  const [revenueModalOpen, setRevenueModalOpen] = useState(false);
  const [selectedRevenueType, setSelectedRevenueType] = useState<string>('');
  const [selectedRevenueTitle, setSelectedRevenueTitle] = useState<string>('');

  const handleRevenueClick = (revenueType: string, title: string) => {
    setSelectedRevenueType(revenueType);
    setSelectedRevenueTitle(title);
    setRevenueModalOpen(true);
  };

  const getDateRange = () => getDateRangeFromPreset(dateRangePreset, customDateRange);

  // Piecewise distributor analytics — each section renders independently.
  // distributorId is `undefined` here so the api client uses the `/me/...`
  // alias that resolves to the caller's own id server-side.
  const headerQuery = useDistributorHeader(undefined, getDateRange());
  const trendsQuery = useDistributorTrends(undefined, getDateRange(), { revenueInterval });
  const rankingsQuery = useDistributorRankings(undefined, getDateRange());

  // Piecewise slices — pulled here, then projected into the same shape the
  // markup below expects. Each slice is typed against DistributorAnalytics
  // so we keep the chart/StatCard props honest.
  type DA = DistributorAnalytics;
  const header = headerQuery.data as
    | Pick<DA, 'networkPhysicians' | 'activePhysicians' | 'totalPatients'
        | 'totalOrders' | 'monthlyOrders' | 'ordersToday' | 'ordersYesterday'
        | 'revenueToday' | 'revenueYesterday' | 'networkRevenue'
        | 'monthlyRevenue' | 'averageOrderValue' | 'ordersByStatus'
        | 'bulkOrdersByStatus' | 'orderBreakdown'>
    | undefined;
  const trends = trendsQuery.data as
    | Pick<DA, 'monthlyTrends' | 'revenueBreakdown'>
    | undefined;
  const rankings = rankingsQuery.data as
    | Pick<DA, 'topPhysicians'>
    | undefined;

  const handleDateRangeChange = (preset: DateRangePreset) => {
    setDateRangePreset(preset);
  };

  // Auxiliary data — phased analytics. Kept on the existing fetch path
  // since it isn't part of the three-slice split.
  const fetchAuxiliaryData = async () => {
    try {
      setError(null);
      const userId = user?._id;
      if (!userId) {
        setPhasedAnalytics(null);
        return;
      }
      const [phasedResponse, rollingThirtyDayPhasedResponse] = await Promise.all([
        apiClient.getDistributorPhasedAnalytics(userId, getDateRange()),
        apiClient.getDistributorPhasedAnalytics(userId, getDateRangeFromPreset('30d', customDateRange)),
      ]);

      if (phasedResponse.success && phasedResponse.data) {
        setPhasedAnalytics(
          mergeThirtyDayRevenue(
            phasedResponse.data,
            rollingThirtyDayPhasedResponse.success && rollingThirtyDayPhasedResponse.data
              ? rollingThirtyDayPhasedResponse.data.summary.totalRevenue
              : undefined
          ) as DistributorPhasedAnalytics
        );
      } else {
        setPhasedAnalytics(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    // Invalidate the three piecewise queries so they refetch with their
    // skeletons; the auxiliary data is fetched the old way.
    queryClient.invalidateQueries({ queryKey: ['distributor-analytics'] });
    await fetchAuxiliaryData();
  };

  useEffect(() => {
    fetchAuxiliaryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangePreset, customDateRange]);

  // Only treat it as a hard error if *every* slice failed; otherwise we let
  // each section show its own state and don't blank the page.
  const hardError = error
    || (headerQuery.isError && trendsQuery.isError && rankingsQuery.isError
      ? (headerQuery.error as Error | undefined)?.message || 'Failed to load analytics'
      : null);

  if (hardError) {
    return (
      <div className="text-center py-8 lg:py-12">
        <XCircle className="w-12 h-12 lg:w-16 lg:h-16 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg lg:text-xl font-medium text-gray-900 mb-2">Failed to load analytics</h3>
        <p className="text-sm lg:text-base text-gray-600 mb-4">{hardError}</p>
        <button
          onClick={handleRefresh}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  // Prepare chart data from the trends + rankings slices, guarding for
  // when they haven't resolved yet.
  const orderStatusData = header
    ? Object.entries(header.ordersByStatus)
        .filter(([_, count]) => count > 0)
        .map(([status, count]) => ({
          name: status.charAt(0).toUpperCase() + status.slice(1),
          value: count,
          color: getStatusColor(status)
        }))
    : [];

  const monthlyTrendsForCalc = trends?.monthlyTrends || [];
  const physicianTrend = monthlyTrendsForCalc.length >= 2 ? {
    value: Math.abs(monthlyTrendsForCalc[monthlyTrendsForCalc.length - 1].physicians -
                   monthlyTrendsForCalc[monthlyTrendsForCalc.length - 2].physicians),
    isPositive: monthlyTrendsForCalc[monthlyTrendsForCalc.length - 1].physicians >=
                monthlyTrendsForCalc[monthlyTrendsForCalc.length - 2].physicians
  } : undefined;

  const orderTrend = monthlyTrendsForCalc.length >= 2 ? {
    value: Math.abs(monthlyTrendsForCalc[monthlyTrendsForCalc.length - 1].orders -
                   monthlyTrendsForCalc[monthlyTrendsForCalc.length - 2].orders),
    isPositive: monthlyTrendsForCalc[monthlyTrendsForCalc.length - 1].orders >=
                monthlyTrendsForCalc[monthlyTrendsForCalc.length - 2].orders
  } : undefined;

  const revenueTrend = monthlyTrendsForCalc.length >= 2 ? {
    value: Math.abs(monthlyTrendsForCalc[monthlyTrendsForCalc.length - 1].revenue -
                   monthlyTrendsForCalc[monthlyTrendsForCalc.length - 2].revenue),
    isPositive: monthlyTrendsForCalc[monthlyTrendsForCalc.length - 1].revenue >=
                monthlyTrendsForCalc[monthlyTrendsForCalc.length - 2].revenue
  } : undefined;

  const phasedMetricConfigs = distributorPhaseMetrics.map(metric => {
    const clickMap: Record<string, { revenueType: string; title: string } | undefined> = {
      distributorMargin: { revenueType: 'distributor', title: 'Your Account Manager Margin' }
    };
    const clickConfig = clickMap[metric.key];
    return {
      ...metric,
      clickable: Boolean(clickConfig),
      onClick: clickConfig
        ? () => handleRevenueClick(clickConfig.revenueType, clickConfig.title)
        : undefined
    };
  });

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* Order Cutoff Notification */}
      <OrderCutoffNotification variant="banner" />

      {/* Header with date range selector and refresh button */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Network Analytics</h1>
            <p className="text-sm lg:text-base text-gray-600">Overview of your physician network performance</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm lg:text-base"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
        <div className="flex justify-start sm:justify-end">
          <DateRangeSelector
            selectedPreset={dateRangePreset}
            onPresetChange={handleDateRangeChange}
            customDateRange={customDateRange}
            onCustomDateRangeChange={setCustomDateRange}
          />
        </div>
      </div>

      {/* Payment Instructions Reminder */}
      {user && !user.distributorData?.paymentInstructions && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 lg:p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm lg:text-base font-medium text-amber-800">
                Payment Instructions Required
              </h3>
              <p className="mt-1 text-sm text-amber-700">
                Please add your payment instructions so administrators can process payments to you.
                This includes wire transfer details, preferred payment methods, and billing information.
              </p>
              <div className="mt-3">
                <button
                  onClick={() => navigate('/settings')}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-amber-800 bg-amber-100 border border-amber-200 rounded-lg hover:bg-amber-200 transition-colors"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Add Payment Instructions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {phasedAnalytics && (
        <PhasedMetricsPanel
          title="Phased Economics View"
          subtitle="Realized, booked, and pipeline revenue layers."
          metrics={phasedMetricConfigs}
          summary={phasedAnalytics.summary}
          variant="dot-cards"
          gridClassName="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6"
        />
      )}

      {/* Orders Today Section — header slice */}
      {header ? (
        <OrdersTodaySection
          ordersToday={header.ordersToday}
          ordersYesterday={header.ordersYesterday}
          revenueToday={header.revenueToday}
          revenueYesterday={header.revenueYesterday}
          onClick={() => navigate('/invoice-tracking')}
        />
      ) : (
        <div className="animate-pulse bg-gray-100 h-32 rounded-2xl" />
      )}

      {/* Stats Cards — header slice (with trend from trends slice once available) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {header && rankings ? (
          <>
            <StatCard
              title="Network Physicians"
              value={header.networkPhysicians.toLocaleString()}
              icon={UserCheck}
              trend={physicianTrend}
              color="bg-green-500"
              bgGradient="bg-gradient-to-br from-green-500 to-green-600"
            />
            <StatCard
              title="Monthly Orders"
              value={header.monthlyOrders.toLocaleString()}
              icon={ShoppingCart}
              trend={orderTrend}
              color="bg-blue-500"
              bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
            />
            <StatCard
              title="Total Network Revenue"
              value={formatCurrency(header.networkRevenue)}
              icon={DollarSign}
              trend={revenueTrend}
              color="bg-yellow-500"
              bgGradient="bg-gradient-to-br from-yellow-500 to-yellow-600"
            />
            <StatCard
              title="Top Physician Orders"
              value={rankings.topPhysicians.length > 0 ?
                rankings.topPhysicians[0].orderCount.toLocaleString() :
                '0'}
              icon={UserCheck}
              color="bg-purple-500"
              bgGradient="bg-gradient-to-br from-purple-500 to-purple-600"
            />
          </>
        ) : (
          [...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse bg-gray-100 h-24 lg:h-32 rounded-2xl" />
          ))
        )}
      </div>

      {/* Clinic Opportunities Card */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-4 lg:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              <h3 className="text-base lg:text-lg font-semibold text-gray-900">Clinic Opportunities</h3>
            </div>
            <span className="text-sm text-gray-500">Clinics who may need to order more</span>
          </div>
        </div>
        <div className="p-4 lg:p-6">
          <ReorderRankingTable
            compact={true}
            limit={5}
            showFilters={false}
            showPagination={false}
            onViewAll={() => navigate('/analytics?tab=reorder-rankings')}
          />
        </div>
      </div>

      {/* Charts and Data */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
        {/* Revenue Trends — trends slice */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <h3 className="text-base lg:text-lg font-semibold text-gray-900">Revenue Trends</h3>
            <TrendIntervalSelector
              selectedInterval={revenueInterval}
              onIntervalChange={setRevenueInterval}
            />
          </div>
          {!trends ? (
            <div className="animate-pulse bg-gray-100 h-64 rounded-xl" />
          ) : (
            <LineChart
              data={trends.monthlyTrends}
              xKey="month"
              lines={[
                { dataKey: 'revenue', color: '#8B5CF6', name: 'Network Revenue' },
                { dataKey: 'orders', color: '#3B82F6', name: 'Network Orders' }
              ]}
              height={250}
            />
          )}
        </div>

        {/* Order Status Distribution — header slice */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
          <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4 lg:mb-6">Network Order Status</h3>
          {!header ? (
            <div className="animate-pulse bg-gray-100 h-64 rounded-xl" />
          ) : (
            <PieChart
              data={orderStatusData}
              height={250}
              colors={[
                '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6',
                '#06B6D4', '#6B7280', '#EF4444', '#DC2626'
              ]}
              showLegend={true}
            />
          )}
        </div>

        {/* Top Physicians — rankings slice */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
          <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4 lg:mb-6">Top Physicians Performance</h3>
          {!rankings ? (
            <div className="animate-pulse bg-gray-100 h-64 rounded-xl" />
          ) : (
            <BarChart
              data={rankings.topPhysicians}
              xKey="physicianName"
              bars={[
                { dataKey: 'orderCount', color: '#3B82F6', name: 'Orders' },
                { dataKey: 'revenue', color: '#10B981', name: 'Revenue ($)' }
              ]}
              height={250}
            />
          )}
        </div>

        {/* Physician Performance Metrics — rankings slice (uses header for ratios) */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
          <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4 lg:mb-6">Physician Performance Insights</h3>

          {!rankings || !header ? (
            <div className="animate-pulse bg-gray-100 h-64 rounded-xl" />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600">Most Active</p>
                      <p className="text-lg font-bold text-blue-900">
                        {rankings.topPhysicians.length > 0 ? rankings.topPhysicians[0].physicianName : 'None'}
                      </p>
                      <p className="text-xs text-blue-600">
                        {rankings.topPhysicians.length > 0 ? `${rankings.topPhysicians[0].orderCount} orders` : '0 orders'}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <UserCheck className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">Avg Orders/Physician</p>
                      <p className="text-lg font-bold text-green-900">
                        {header.networkPhysicians > 0 ?
                          Math.round(header.totalOrders / header.networkPhysicians) : 0}
                      </p>
                      <p className="text-xs text-green-600">This period</p>
                    </div>
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-600">Active Rate</p>
                      <p className="text-lg font-bold text-purple-900">
                        {header.networkPhysicians > 0 ?
                          Math.round((header.activePhysicians / header.networkPhysicians) * 100) : 0}%
                      </p>
                      <p className="text-xs text-purple-600">{header.activePhysicians}/{header.networkPhysicians} active</p>
                    </div>
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Activity className="h-5 w-5 text-purple-600" />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Top Performing Physicians</h4>
                <div className="space-y-3">
                  {rankings.topPhysicians.slice(0, 5).map((physician, index) => (
                    <div key={physician.physicianName} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">#{index + 1}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{physician.physicianName}</p>
                          <p className="text-xs text-gray-500">{physician.patientCount} patients • {formatCurrency(physician.revenue)} total volume</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900">{physician.orderCount} orders</p>
                        <p className="text-xs text-gray-500">Avg: {formatCurrency(physician.orderCount > 0 ? physician.revenue / physician.orderCount : 0)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Revenue Breakdown Modal */}
      <RevenueBreakdownModal
        isOpen={revenueModalOpen}
        onClose={() => setRevenueModalOpen(false)}
        revenueType={selectedRevenueType}
        title={selectedRevenueTitle}
        dateRange={getDateRange()}
      />
    </div>
  );
};

// Supervisor dashboard — same layout as the legacy supervisor view but the
// payload (per-AM revenue rollup + top-physician $lookup across the full
// network) is split into three independent React Query slices so the page
// can render in stages instead of gating everything on the slowest piece.
// The combined endpoint is still used by Analytics.tsx.
const SupervisorDashboard: React.FC = () => {
  const user = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [phasedAnalytics, setPhasedAnalytics] = useState<SupervisorPhasedAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('30d');
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange>(getDefaultCustomDateRange());
  const [revenueInterval, setRevenueInterval] = useState<TrendInterval>('monthly');

  // Revenue breakdown modal state
  const [revenueModalOpen, setRevenueModalOpen] = useState(false);
  const [selectedRevenueType, setSelectedRevenueType] = useState<string>('');
  const [selectedRevenueTitle, setSelectedRevenueTitle] = useState<string>('');

  const handleRevenueClick = (revenueType: string, title: string) => {
    setSelectedRevenueType(revenueType);
    setSelectedRevenueTitle(title);
    setRevenueModalOpen(true);
  };

  const getDateRange = () => getDateRangeFromPreset(dateRangePreset, customDateRange);

  // Piecewise supervisor analytics — each hook drives its own section.
  // Query keys include the date range + revenue interval so re-selecting
  // a preset transparently refetches only the affected slices.
  const headerQuery = useSupervisorHeader(getDateRange());
  const trendsQuery = useSupervisorTrends(getDateRange(), { revenueInterval });
  const rankingsQuery = useSupervisorRankings(getDateRange());

  const header = headerQuery.data as SupervisorAnalyticsHeader | undefined;
  const trends = trendsQuery.data as SupervisorAnalyticsTrends | undefined;
  const rankings = rankingsQuery.data as SupervisorAnalyticsRankings | undefined;

  // Phased analytics — kept as a separate parallel fetch (not part of the
  // three-slice refactor); same shape as the supervisor analytics page.
  const fetchPhasedData = async () => {
    try {
      setError(null);
      const [phasedResponse, rollingThirtyDayPhasedResponse] = await Promise.all([
        apiClient.getSupervisorPhasedAnalytics(getDateRange()),
        apiClient.getSupervisorPhasedAnalytics(getDateRangeFromPreset('30d', customDateRange)),
      ]);

      if (phasedResponse.success && phasedResponse.data) {
        setPhasedAnalytics(
          mergeThirtyDayRevenue(
            phasedResponse.data,
            rollingThirtyDayPhasedResponse.success && rollingThirtyDayPhasedResponse.data
              ? rollingThirtyDayPhasedResponse.data.summary.totalRevenue
              : undefined
          ) as SupervisorPhasedAnalytics
        );
      } else {
        setPhasedAnalytics(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    // Invalidate the three piecewise queries so they refetch with their
    // skeletons; the phased data is fetched the old way.
    queryClient.invalidateQueries({ queryKey: ['supervisor-analytics'] });
    await fetchPhasedData();
  };

  const handleDateRangeChange = (preset: DateRangePreset) => {
    setDateRangePreset(preset);
  };

  useEffect(() => {
    fetchPhasedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangePreset, customDateRange]);

  // Hard error: only show the full-page error when every slice failed.
  // Per-slice failures degrade gracefully — each section shows its own
  // skeleton/empty state.
  const hardError = error
    || (headerQuery.isError && trendsQuery.isError && rankingsQuery.isError
      ? (headerQuery.error as Error | undefined)?.message || 'Failed to load analytics'
      : null);

  if (hardError) {
    return (
      <div className="text-center py-8 lg:py-12">
        <XCircle className="w-12 h-12 lg:w-16 lg:h-16 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg lg:text-xl font-medium text-gray-900 mb-2">Failed to load analytics</h3>
        <p className="text-sm lg:text-base text-gray-600 mb-4">{hardError}</p>
        <button
          onClick={handleRefresh}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  // Order status pie data — depends on the header slice.
  const orderStatusData = header
    ? Object.entries(header.ordersByStatus)
        .filter(([_, count]) => count > 0)
        .map(([status, count]) => ({
          name: status.charAt(0).toUpperCase() + status.slice(1),
          value: count,
          color: getStatusColor(status)
        }))
    : [];

  // Month-over-month trend deltas — depend on the trends slice.
  const monthlyTrends = trends?.monthlyTrends ?? [];
  const physicianTrend = monthlyTrends.length >= 2 ? {
    value: Math.abs(monthlyTrends[monthlyTrends.length - 1].physicians -
                   monthlyTrends[monthlyTrends.length - 2].physicians),
    isPositive: monthlyTrends[monthlyTrends.length - 1].physicians >=
                monthlyTrends[monthlyTrends.length - 2].physicians
  } : undefined;

  const orderTrend = monthlyTrends.length >= 2 ? {
    value: Math.abs(monthlyTrends[monthlyTrends.length - 1].orders -
                   monthlyTrends[monthlyTrends.length - 2].orders),
    isPositive: monthlyTrends[monthlyTrends.length - 1].orders >=
                monthlyTrends[monthlyTrends.length - 2].orders
  } : undefined;

  const revenueTrend = monthlyTrends.length >= 2 ? {
    value: Math.abs(monthlyTrends[monthlyTrends.length - 1].revenue -
                   monthlyTrends[monthlyTrends.length - 2].revenue),
    isPositive: monthlyTrends[monthlyTrends.length - 1].revenue >=
                monthlyTrends[monthlyTrends.length - 2].revenue
  } : undefined;

  const phasedMetricConfigs = supervisorPhaseMetrics.map(metric => {
    const clickMap: Record<string, { revenueType: string; title: string } | undefined> = {
      supervisorMargin: { revenueType: 'supervisor', title: 'Your Supervisor Margin' },
      distributorMargin: { revenueType: 'distributor', title: 'Account Manager Margins' }
    };

    const clickConfig = clickMap[metric.key];

    return {
      ...metric,
      clickable: Boolean(clickConfig),
      onClick: clickConfig
        ? () => handleRevenueClick(clickConfig.revenueType, clickConfig.title)
        : undefined
    };
  });

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* Order Cutoff Notification */}
      <OrderCutoffNotification variant="banner" />

      {/* Header with date range selector and refresh button */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Network Analytics</h1>
            <p className="text-sm lg:text-base text-gray-600">Overview of your physician network performance</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm lg:text-base"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
        <div className="flex justify-start sm:justify-end">
          <DateRangeSelector
            selectedPreset={dateRangePreset}
            onPresetChange={handleDateRangeChange}
            customDateRange={customDateRange}
            onCustomDateRangeChange={setCustomDateRange}
          />
        </div>
      </div>

      {/* Payment Instructions Reminder */}
      {user && !user.distributorData?.paymentInstructions && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 lg:p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm lg:text-base font-medium text-amber-800">
                Payment Instructions Required
              </h3>
              <p className="mt-1 text-sm text-amber-700">
                Please add your payment instructions so administrators can process payments to you.
                This includes wire transfer details, preferred payment methods, and billing information.
              </p>
              <div className="mt-3">
                <button
                  onClick={() => navigate('/settings')}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-amber-800 bg-amber-100 border border-amber-200 rounded-lg hover:bg-amber-200 transition-colors"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Add Payment Instructions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {phasedAnalytics && (
        <PhasedMetricsPanel
          title="Phased Economics View"
          subtitle="Realized, booked, and pipeline revenue layers."
          metrics={phasedMetricConfigs}
          summary={phasedAnalytics.summary}
          variant="dot-cards"
          gridClassName="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6"
        />
      )}

      {/* Orders Today Section — header slice. */}
      {header ? (
        <OrdersTodaySection
          ordersToday={header.ordersToday}
          ordersYesterday={header.ordersYesterday}
          revenueToday={header.revenueToday}
          revenueYesterday={header.revenueYesterday}
          onClick={() => navigate('/invoice-tracking')}
        />
      ) : (
        <div className="animate-pulse bg-gray-100 h-24 lg:h-32 rounded-2xl" />
      )}

      {/* Stats Cards — header slice + trend deltas from trends slice. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {header ? (
          <>
            <StatCard
              title="Network Physicians"
              value={header.networkPhysicians.toLocaleString()}
              icon={UserCheck}
              trend={physicianTrend}
              color="bg-green-500"
              bgGradient="bg-gradient-to-br from-green-500 to-green-600"
            />
            <StatCard
              title="Monthly Orders"
              value={header.monthlyOrders.toLocaleString()}
              icon={ShoppingCart}
              trend={orderTrend}
              color="bg-blue-500"
              bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
            />
            <StatCard
              title="Total Network Revenue"
              value={formatCurrency(header.networkRevenue)}
              icon={DollarSign}
              trend={revenueTrend}
              color="bg-yellow-500"
              bgGradient="bg-gradient-to-br from-yellow-500 to-yellow-600"
            />
            <StatCard
              title="Top Physician Orders"
              value={rankings && rankings.topPhysicians.length > 0
                ? rankings.topPhysicians[0].orderCount.toLocaleString()
                : '0'}
              icon={UserCheck}
              color="bg-purple-500"
              bgGradient="bg-gradient-to-br from-purple-500 to-purple-600"
            />
          </>
        ) : (
          [...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse bg-gray-100 h-24 lg:h-32 rounded-2xl" />
          ))
        )}
      </div>

      {/* Clinic Opportunities Card */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-4 lg:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              <h3 className="text-base lg:text-lg font-semibold text-gray-900">Clinic Opportunities</h3>
            </div>
            <span className="text-sm text-gray-500">Clinics who may need to order more</span>
          </div>
        </div>
        <div className="p-4 lg:p-6">
          <ReorderRankingTable
            compact={true}
            limit={5}
            showFilters={false}
            showPagination={false}
            onViewAll={() => navigate('/analytics?tab=reorder-rankings')}
          />
        </div>
      </div>

      {/* Charts and Data */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
        {/* Revenue Trends — trends slice. */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <h3 className="text-base lg:text-lg font-semibold text-gray-900">Revenue Trends</h3>
            <TrendIntervalSelector
              selectedInterval={revenueInterval}
              onIntervalChange={setRevenueInterval}
            />
          </div>
          {!trends ? (
            <div className="animate-pulse bg-gray-100 h-64 rounded-xl" />
          ) : (
            <LineChart
              data={trends.monthlyTrends}
              xKey="month"
              lines={[
                { dataKey: 'revenue', color: '#8B5CF6', name: 'Network Revenue' },
                { dataKey: 'orders', color: '#3B82F6', name: 'Network Orders' }
              ]}
              height={250}
            />
          )}
        </div>

        {/* Order Status Distribution — header slice. */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
          <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4 lg:mb-6">Network Order Status</h3>
          {!header ? (
            <div className="animate-pulse bg-gray-100 h-64 rounded-xl" />
          ) : (
            <PieChart
              data={orderStatusData}
              height={250}
              colors={[
                '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6',
                '#06B6D4', '#6B7280', '#EF4444', '#DC2626'
              ]}
              showLegend={true}
            />
          )}
        </div>

        {/* Top Physicians — rankings slice (slowest). */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
          <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4 lg:mb-6">Top Physicians Performance</h3>
          {!rankings ? (
            <div className="animate-pulse bg-gray-100 h-64 rounded-xl" />
          ) : (
            <BarChart
              data={rankings.topPhysicians}
              xKey="physicianName"
              bars={[
                { dataKey: 'orderCount', color: '#3B82F6', name: 'Orders' },
                { dataKey: 'revenue', color: '#10B981', name: 'Revenue ($)' }
              ]}
              height={250}
            />
          )}
        </div>

        {/* Physician Performance Metrics — combines header + rankings slices. */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
          <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4 lg:mb-6">Physician Performance Insights</h3>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Most Active</p>
                  <p className="text-lg font-bold text-blue-900">
                    {rankings && rankings.topPhysicians.length > 0 ? rankings.topPhysicians[0].physicianName : 'None'}
                  </p>
                  <p className="text-xs text-blue-600">
                    {rankings && rankings.topPhysicians.length > 0 ? `${rankings.topPhysicians[0].orderCount} orders` : '0 orders'}
                  </p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <UserCheck className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Avg Orders/Physician</p>
                  <p className="text-lg font-bold text-green-900">
                    {header && header.networkPhysicians > 0
                      ? Math.round(header.totalOrders / header.networkPhysicians)
                      : 0}
                  </p>
                  <p className="text-xs text-green-600">This period</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-purple-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">Active Rate</p>
                  <p className="text-lg font-bold text-purple-900">
                    {header && header.networkPhysicians > 0
                      ? Math.round((header.activePhysicians / header.networkPhysicians) * 100)
                      : 0}%
                  </p>
                  <p className="text-xs text-purple-600">
                    {header ? `${header.activePhysicians}/${header.networkPhysicians} active` : '—'}
                  </p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Activity className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Top Physicians List — rankings slice. */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Top Performing Physicians</h4>
            {!rankings ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse bg-gray-100 h-16 rounded-xl" />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {rankings.topPhysicians.slice(0, 5).map((physician, index) => (
                  <div key={physician.physicianName} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">#{index + 1}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{physician.physicianName}</p>
                        <p className="text-xs text-gray-500">{physician.patientCount} patients • {formatCurrency(physician.revenue)} total volume</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">{physician.orderCount} orders</p>
                      <p className="text-xs text-gray-500">Avg: {formatCurrency(physician.orderCount > 0 ? physician.revenue / physician.orderCount : 0)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Revenue Breakdown Modal */}
      <RevenueBreakdownModal
        isOpen={revenueModalOpen}
        onClose={() => setRevenueModalOpen(false)}
        revenueType={selectedRevenueType}
        title={selectedRevenueTitle}
        dateRange={getDateRange()}
      />
    </div>
  );
};

const DistributorDashboard: React.FC = () => {
  const user = useUser();
  // Distributors and supervisors now both have piecewise render paths.
  // The legacy combined-fetch fallback is only kept for safety; nothing
  // routes to it in normal operation.
  if (user?.role === 'distributor') {
    return <DistributorOwnDashboard />;
  }
  if (user?.role === 'supervisor') {
    return <SupervisorDashboard />;
  }
  return <DistributorOrSupervisorLegacyDashboard />;
};

const DistributorOrSupervisorLegacyDashboard: React.FC = () => {
  const user = useUser();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<DistributorAnalytics | null>(null);
  const [phasedAnalytics, setPhasedAnalytics] = useState<DistributorPhasedAnalytics | SupervisorPhasedAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('30d');
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange>(getDefaultCustomDateRange());
  const [revenueInterval, setRevenueInterval] = useState<TrendInterval>('monthly');

  // Revenue breakdown modal state
  const [revenueModalOpen, setRevenueModalOpen] = useState(false);
  const [selectedRevenueType, setSelectedRevenueType] = useState<string>('');
  const [selectedRevenueTitle, setSelectedRevenueTitle] = useState<string>('');

  const handleRevenueClick = (revenueType: string, title: string) => {
    setSelectedRevenueType(revenueType);
    setSelectedRevenueTitle(title);
    setRevenueModalOpen(true);
  };

  // Get date range from preset (default 30 days)
  const getDateRange = () => getDateRangeFromPreset(dateRangePreset, customDateRange);

  const fetchAnalytics = async () => {
    try {
      setError(null);
      const userId = user?._id;
      const [response, phasedResponse, rollingThirtyDayPhasedResponse] = await Promise.all([
        user?.role === 'supervisor'
          ? apiClient.getSupervisorAnalytics(getDateRange(), { revenueInterval })
          : apiClient.getDistributorAnalytics(undefined, getDateRange(), { revenueInterval }),
        user?.role === 'supervisor'
          ? apiClient.getSupervisorPhasedAnalytics(getDateRange())
          : userId
            ? apiClient.getDistributorPhasedAnalytics(userId, getDateRange())
            : Promise.resolve({ success: false, error: 'Missing distributor id' } as any),
        user?.role === 'supervisor'
          ? apiClient.getSupervisorPhasedAnalytics(getDateRangeFromPreset('30d', customDateRange))
          : userId
            ? apiClient.getDistributorPhasedAnalytics(userId, getDateRangeFromPreset('30d', customDateRange))
            : Promise.resolve({ success: false, error: 'Missing distributor id' } as any)
      ]);

      if (response.success && response.data) {
        setAnalytics(response.data);
        if (phasedResponse.success && phasedResponse.data) {
          setPhasedAnalytics(
            mergeThirtyDayRevenue(
              phasedResponse.data,
              rollingThirtyDayPhasedResponse.success && rollingThirtyDayPhasedResponse.data
                ? rollingThirtyDayPhasedResponse.data.summary.totalRevenue
                : undefined
            )
          );
        } else {
          setPhasedAnalytics(null);
        }
      } else {
        setError(response.error || 'Failed to load analytics');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
  };

  const handleDateRangeChange = (preset: DateRangePreset) => {
    setDateRangePreset(preset);
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRangePreset, customDateRange, revenueInterval]);

  if (loading) {
    return (
      <div className="space-y-6 lg:space-y-8 animate-pulse">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-200 h-24 lg:h-32 rounded-2xl"></div>
          ))}
          </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-200 h-48 lg:h-64 rounded-2xl"></div>
          ))}
        </div>
                </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 lg:py-12">
        <XCircle className="w-12 h-12 lg:w-16 lg:h-16 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg lg:text-xl font-medium text-gray-900 mb-2">Failed to load analytics</h3>
        <p className="text-sm lg:text-base text-gray-600 mb-4">{error}</p>
        <button
          onClick={fetchAnalytics}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
              </div>
    );
  }

  if (!analytics) {
    return <div className="text-center py-8 lg:py-12 text-gray-500">No analytics data available</div>;
  }

  // Prepare data for charts
  const orderStatusData = Object.entries(analytics.ordersByStatus)
    .filter(([_, count]) => count > 0)
    .map(([status, count]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: count,
      color: getStatusColor(status)
    }));

  // Calculate trends
  const physicianTrend = analytics.monthlyTrends.length >= 2 ? {
    value: Math.abs(analytics.monthlyTrends[analytics.monthlyTrends.length - 1].physicians - 
                   analytics.monthlyTrends[analytics.monthlyTrends.length - 2].physicians),
    isPositive: analytics.monthlyTrends[analytics.monthlyTrends.length - 1].physicians >= 
                analytics.monthlyTrends[analytics.monthlyTrends.length - 2].physicians
  } : undefined;

  const orderTrend = analytics.monthlyTrends.length >= 2 ? {
    value: Math.abs(analytics.monthlyTrends[analytics.monthlyTrends.length - 1].orders - 
                   analytics.monthlyTrends[analytics.monthlyTrends.length - 2].orders),
    isPositive: analytics.monthlyTrends[analytics.monthlyTrends.length - 1].orders >= 
                analytics.monthlyTrends[analytics.monthlyTrends.length - 2].orders
  } : undefined;

  const revenueTrend = analytics.monthlyTrends.length >= 2 ? {
    value: Math.abs(analytics.monthlyTrends[analytics.monthlyTrends.length - 1].revenue - 
                   analytics.monthlyTrends[analytics.monthlyTrends.length - 2].revenue),
    isPositive: analytics.monthlyTrends[analytics.monthlyTrends.length - 1].revenue >= 
                analytics.monthlyTrends[analytics.monthlyTrends.length - 2].revenue
  } : undefined;

  const phasedMetricConfigs = (user?.role === 'supervisor' ? supervisorPhaseMetrics : distributorPhaseMetrics).map(metric => {
    const clickMap: Record<string, { revenueType: string; title: string } | undefined> = user?.role === 'supervisor'
      ? {
          supervisorMargin: { revenueType: 'supervisor', title: 'Your Supervisor Margin' },
          distributorMargin: { revenueType: 'distributor', title: 'Account Manager Margins' }
        }
      : {
          distributorMargin: { revenueType: 'distributor', title: 'Your Account Manager Margin' }
        };

    const clickConfig = clickMap[metric.key];

    return {
      ...metric,
      clickable: Boolean(clickConfig),
      onClick: clickConfig
        ? () => handleRevenueClick(clickConfig.revenueType, clickConfig.title)
        : undefined
    };
  });

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* Order Cutoff Notification */}
      <OrderCutoffNotification variant="banner" />

      {/* Header with date range selector and refresh button */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Network Analytics</h1>
            <p className="text-sm lg:text-base text-gray-600">Overview of your physician network performance</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm lg:text-base"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
        <div className="flex justify-start sm:justify-end">
          <DateRangeSelector
            selectedPreset={dateRangePreset}
            onPresetChange={handleDateRangeChange}
            customDateRange={customDateRange}
            onCustomDateRangeChange={setCustomDateRange}
          />
        </div>
      </div>

      {/* Payment Instructions Reminder */}
      {user && !user.distributorData?.paymentInstructions && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 lg:p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm lg:text-base font-medium text-amber-800">
                Payment Instructions Required
              </h3>
              <p className="mt-1 text-sm text-amber-700">
                Please add your payment instructions so administrators can process payments to you.
                This includes wire transfer details, preferred payment methods, and billing information.
              </p>
              <div className="mt-3">
                <button
                  onClick={() => navigate('/settings')}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-amber-800 bg-amber-100 border border-amber-200 rounded-lg hover:bg-amber-200 transition-colors"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Add Payment Instructions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
          
      {phasedAnalytics && (
        <PhasedMetricsPanel
          title="Phased Economics View"
          subtitle="Realized, booked, and pipeline revenue layers."
          metrics={phasedMetricConfigs}
          summary={phasedAnalytics.summary}
          variant="dot-cards"
          gridClassName="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6"
        />
      )}

      {/* Orders Today Section */}
      <OrdersTodaySection
        ordersToday={analytics.ordersToday}
        ordersYesterday={analytics.ordersYesterday}
        revenueToday={analytics.revenueToday}
        revenueYesterday={analytics.revenueYesterday}
        onClick={() => navigate('/invoice-tracking')}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <StatCard
          title="Network Physicians"
          value={analytics.networkPhysicians.toLocaleString()}
          icon={UserCheck}
          trend={physicianTrend}
          color="bg-green-500"
          bgGradient="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          title="Monthly Orders"
          value={analytics.monthlyOrders.toLocaleString()}
          icon={ShoppingCart}
          trend={orderTrend}
          color="bg-blue-500"
          bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          title="Total Network Revenue"
          value={formatCurrency(analytics.networkRevenue)}
          icon={DollarSign}
          trend={revenueTrend}
          color="bg-yellow-500"
          bgGradient="bg-gradient-to-br from-yellow-500 to-yellow-600"
        />
        <StatCard
          title="Top Physician Orders"
          value={analytics.topPhysicians.length > 0 ?
            analytics.topPhysicians[0].orderCount.toLocaleString() :
            '0'}
          icon={UserCheck}
          color="bg-purple-500"
          bgGradient="bg-gradient-to-br from-purple-500 to-purple-600"
        />
      </div>

      {/* Clinic Opportunities Card */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-4 lg:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              <h3 className="text-base lg:text-lg font-semibold text-gray-900">Clinic Opportunities</h3>
            </div>
            <span className="text-sm text-gray-500">Clinics who may need to order more</span>
          </div>
        </div>
        <div className="p-4 lg:p-6">
          <ReorderRankingTable
            compact={true}
            limit={5}
            showFilters={false}
            showPagination={false}
            onViewAll={() => navigate('/analytics?tab=reorder-rankings')}
          />
        </div>
      </div>

      {/* Charts and Data */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
        {/* Revenue Trends */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <h3 className="text-base lg:text-lg font-semibold text-gray-900">Revenue Trends</h3>
            <TrendIntervalSelector
              selectedInterval={revenueInterval}
              onIntervalChange={setRevenueInterval}
            />
          </div>
                     <LineChart
             data={analytics.monthlyTrends}
             xKey="month"
             lines={[
               { dataKey: 'revenue', color: '#8B5CF6', name: 'Network Revenue' },
               { dataKey: 'orders', color: '#3B82F6', name: 'Network Orders' }
             ]}
             height={250}
           />
         </div>

         {/* Order Status Distribution */}
         <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
           <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4 lg:mb-6">Network Order Status</h3>
           <PieChart
             data={orderStatusData}
             height={250}
             colors={[
               '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', 
               '#06B6D4', '#6B7280', '#EF4444', '#DC2626'
             ]}
             showLegend={true}
           />
         </div>

         {/* Top Physicians */}
         <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
           <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4 lg:mb-6">Top Physicians Performance</h3>
           <BarChart
             data={analytics.topPhysicians}
             xKey="physicianName"
             bars={[
               { dataKey: 'orderCount', color: '#3B82F6', name: 'Orders' },
               { dataKey: 'revenue', color: '#10B981', name: 'Revenue ($)' }
             ]}
             height={250}
           />
        </div>

        {/* Physician Performance Metrics */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
          <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4 lg:mb-6">Physician Performance Insights</h3>
          
          {/* Key Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Most Active</p>
                  <p className="text-lg font-bold text-blue-900">
                    {analytics.topPhysicians.length > 0 ? analytics.topPhysicians[0].physicianName : 'None'}
                  </p>
                  <p className="text-xs text-blue-600">
                    {analytics.topPhysicians.length > 0 ? `${analytics.topPhysicians[0].orderCount} orders` : '0 orders'}
                  </p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <UserCheck className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Avg Orders/Physician</p>
                  <p className="text-lg font-bold text-green-900">
                    {analytics.networkPhysicians > 0 ? 
                      Math.round(analytics.totalOrders / analytics.networkPhysicians) : 0}
                  </p>
                  <p className="text-xs text-green-600">This period</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </div>
            
            <div className="bg-purple-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">Active Rate</p>
                  <p className="text-lg font-bold text-purple-900">
                    {analytics.networkPhysicians > 0 ? 
                      Math.round((analytics.activePhysicians / analytics.networkPhysicians) * 100) : 0}%
                  </p>
                  <p className="text-xs text-purple-600">{analytics.activePhysicians}/{analytics.networkPhysicians} active</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Activity className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Top Physicians List */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Top Performing Physicians</h4>
            <div className="space-y-3">
              {analytics.topPhysicians.slice(0, 5).map((physician, index) => (
                <div key={physician.physicianName} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">#{index + 1}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{physician.physicianName}</p>
                      <p className="text-xs text-gray-500">{physician.patientCount} patients • {formatCurrency(physician.revenue)} total volume</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">{physician.orderCount} orders</p>
                    <p className="text-xs text-gray-500">Avg: {formatCurrency(physician.orderCount > 0 ? physician.revenue / physician.orderCount : 0)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Breakdown Modal */}
      <RevenueBreakdownModal
        isOpen={revenueModalOpen}
        onClose={() => setRevenueModalOpen(false)}
        revenueType={selectedRevenueType}
        title={selectedRevenueTitle}
        dateRange={getDateRange()}
      />
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [phasedAnalytics, setPhasedAnalytics] = useState<AdminPhasedAnalytics | null>(null);
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('30d');
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange>(getDefaultCustomDateRange());
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Trend interval state for charts
  const [userGrowthInterval, setUserGrowthInterval] = useState<TrendInterval>('weekly');
  const [revenueInterval, setRevenueInterval] = useState<TrendInterval>('weekly');

  // Operational reports state
  const [dailyOrders, setDailyOrders] = useState<{ date: string; total: number; byStatus: Record<string, number> }[]>([]);
  const [fulfillment, setFulfillment] = useState<{
    statusCounts: Record<string, number>;
    aging: Record<string, { count: number; avgDaysInStatus: number; oldestDays: number }>;
    stuck: { orderId: string; orderNumber: string; status: string; physicianName: string; daysInStatus: number; lastUpdatedAt: string }[];
  } | null>(null);

  // Revenue breakdown modal state
  const [revenueModalOpen, setRevenueModalOpen] = useState(false);
  const [selectedRevenueType, setSelectedRevenueType] = useState<string>('');
  const [selectedRevenueTitle, setSelectedRevenueTitle] = useState<string>('');

  const handleRevenueClick = (revenueType: string, title: string) => {
    setSelectedRevenueType(revenueType);
    setSelectedRevenueTitle(title);
    setRevenueModalOpen(true);
  };

  // Format x-axis labels based on interval type
  const formatIntervalLabel = (_interval: TrendInterval) => (value: string) => {
    if (!value) return '';

    // Weekly format: '2025-W03' -> 'W3'
    if (value.includes('-W')) {
      const weekMatch = value.match(/-W(\d+)$/);
      if (weekMatch) {
        return `W${parseInt(weekMatch[1])}`;
      }
    }

    // Daily format: '2025-01-15' -> 'Jan 15' (Eastern timezone)
    if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return formatDayString(value);
    }

    // Monthly format: '2025-01' -> 'Jan 25' (Eastern timezone)
    if (value.match(/^\d{4}-\d{2}$/)) {
      return formatMonthString(value);
    }

    return value;
  };

  // Get date range from preset (default 30 days)
  const getDateRange = () => getDateRangeFromPreset(dateRangePreset, customDateRange);

  // Piecewise admin analytics — each section renders independently. The
  // hooks share React Query's cache, so the underlying useEffect chain
  // that re-fires on dateRange/interval change is replaced by query-key
  // dependencies. The combined endpoint is still used by other pages.
  const headerQuery = useAdminHeader(getDateRange());
  const trendsQuery = useAdminTrends(getDateRange(), { userGrowthInterval, revenueInterval });
  const rankingsQuery = useAdminRankings(getDateRange());

  const header = headerQuery.data as AdminAnalyticsHeader | undefined;
  const trends = trendsQuery.data as AdminAnalyticsTrends | undefined;
  const rankings = rankingsQuery.data as AdminAnalyticsRankings | undefined;

  const handleDateRangeChange = (preset: DateRangePreset) => {
    setDateRangePreset(preset);
  };

  // Fetch the pieces that aren't part of the three-slice refactor: phased
  // analytics, daily orders, and fulfillment. These keep their existing
  // parallel-fetch behavior and only refire when their inputs change.
  const fetchAuxiliaryData = async () => {
    try {
      setError(null);
      // The rolling-30d phased call only exists to backfill thirtyDayRevenue;
      // when the selected preset is already 30d it's the identical request,
      // so reuse the main response instead of fetching phases twice.
      const rollingThirtyDayPromise = dateRangePreset === '30d'
        ? null
        : apiClient.getSystemPhasedAnalytics(getDateRangeFromPreset('30d', customDateRange));
      const [phasedResponse, dailyOrdersResponse, fulfillmentResponse] = await Promise.all([
        apiClient.getSystemPhasedAnalytics(getDateRange()),
        apiClient.getDailyOrderVolume(30),
        apiClient.getFulfillmentSnapshot(5)
      ]);
      const rollingThirtyDayPhasedResponse = rollingThirtyDayPromise ? await rollingThirtyDayPromise : phasedResponse;

      if (dailyOrdersResponse.success && dailyOrdersResponse.data) {
        setDailyOrders(dailyOrdersResponse.data.days);
      }
      if (fulfillmentResponse.success && fulfillmentResponse.data) {
        setFulfillment(fulfillmentResponse.data);
      }

      if (phasedResponse.success && phasedResponse.data) {
        setPhasedAnalytics(
          mergeThirtyDayRevenue(
            phasedResponse.data,
            rollingThirtyDayPhasedResponse.success && rollingThirtyDayPhasedResponse.data
              ? rollingThirtyDayPhasedResponse.data.summary.totalRevenue
              : undefined
          )
        );
      } else {
        setPhasedAnalytics(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    // Invalidate the three piecewise queries so they refetch with their
    // skeletons; the auxiliary data is fetched the old way.
    queryClient.invalidateQueries({ queryKey: ['admin-analytics'] });
    await fetchAuxiliaryData();
  };

  useEffect(() => {
    fetchAuxiliaryData();
    // Interval selectors only affect the React Query trends hook (its query
    // key includes them) — refetching phases/daily/fulfillment on interval
    // change was wasted load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangePreset, customDateRange]);

  // Section-level error: if every slice failed we still want a clear error
  // UI, but if just one slice fails we let the section show its own state.
  const hardError = error
    || (headerQuery.isError && trendsQuery.isError && rankingsQuery.isError
      ? (headerQuery.error as Error | undefined)?.message || 'Failed to load analytics'
      : null);

  if (hardError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
        <div className="flex items-center space-x-3">
          <XCircle className="h-6 w-6 text-red-500" />
          <div>
            <h3 className="text-lg font-semibold text-red-900">Analytics Error</h3>
            <p className="text-red-700">{hardError}</p>
            </div>
          </div>
        <button
          onClick={handleRefresh}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
            </div>
    );
  }

  // Prepare data for charts (trends slice)
  const userGrowthData = (trends?.userGrowth || []).map(growth => ({
    month: growth.month,
    users: growth.users,
    physicians: growth.physicians,
    distributors: growth.distributors
  }));

  const revenueData = (trends?.revenueByMonth || []).map((revenue: any) => ({
    month: revenue.month,
    revenue: revenue.revenue,
    orders: revenue.orders,
    expenses: revenue.expenses || 0,
    distributorExpenses: revenue.distributorExpenses || 0,
    physicianExpenses: revenue.physicianExpenses || 0
  }));

  const topAccountManagersData = (rankings?.topDistributors || []).slice(0, 5).map(distributor => ({
    name: distributor.distributorName,
    physicians: distributor.physicianCount,
    orders: distributor.orderCount,
    revenue: distributor.revenue
  }));

  const topPhysiciansData = (rankings?.topPhysicians || []).slice(0, 5).map(physician => ({
    name: physician.physicianName,
    orders: physician.orderCount,
    revenue: physician.revenue
  }));

  const phasedMetricConfigs = adminPhaseMetrics.map(metric => {
    const clickMap: Record<string, { revenueType: string; title: string }> = {
      pharmacyCost: { revenueType: 'pharmacy', title: 'Pharmacy Cost' },
      adminMargin: { revenueType: 'admin', title: 'Admin Margin' },
      supervisorMargin: { revenueType: 'supervisor', title: 'Supervisor Margin' },
      distributorMargin: { revenueType: 'distributor', title: 'Account Manager Margin' },
      totalRevenue: { revenueType: 'total', title: 'Total Revenue' }
    };

    const clickConfig = clickMap[metric.key];

    return {
      ...metric,
      clickable: Boolean(clickConfig),
      onClick: clickConfig
        ? () => handleRevenueClick(clickConfig.revenueType, clickConfig.title)
        : undefined
    };
  });


  return (
    <div className="space-y-8">
      {/* Header with date range selector and refresh button */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">System Analytics</h1>
            <p className="text-sm lg:text-base text-gray-600">Complete overview of platform performance and growth</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm lg:text-base"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
        <div className="flex justify-start sm:justify-end">
          <DateRangeSelector
            selectedPreset={dateRangePreset}
            onPresetChange={handleDateRangeChange}
            customDateRange={customDateRange}
            onCustomDateRangeChange={setCustomDateRange}
          />
        </div>
      </div>

      {/* Header slice — KPI strip + orders-today summary. Renders independently
          as soon as /analytics/system/header resolves. */}
      {header ? (
        <OrdersTodaySection
          ordersToday={header.ordersToday}
          ordersYesterday={header.ordersYesterday}
          revenueToday={header.revenueToday}
          revenueYesterday={header.revenueYesterday}
          onClick={() => navigate('/admin/orders')}
        />
      ) : (
        <div className="animate-pulse bg-gray-100 h-32 rounded-2xl" />
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {header ? (
          <>
            <StatCard
              title="Total Users"
              value={header.totalUsers.toLocaleString()}
              icon={Users}
              color="bg-blue-500"
              bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
            />
            <StatCard
              title="Account Managers"
              value={header.totalDistributors.toLocaleString()}
              icon={Building2}
              color="bg-purple-500"
              bgGradient="bg-gradient-to-br from-purple-500 to-purple-600"
              onClick={() => navigate('/distributors')}
            />
            <StatCard
              title="Total Physicians"
              value={header.totalPhysicians.toLocaleString()}
              icon={UserCheck}
              color="bg-green-500"
              bgGradient="bg-gradient-to-br from-green-500 to-green-600"
              onClick={() => navigate('/physicians')}
            />
            <div
              className="relative overflow-hidden rounded-2xl p-4 lg:p-6 bg-gradient-to-br from-yellow-500 to-yellow-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => handleRevenueClick('total', 'System Revenue')}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-yellow-100">30D Revenue</h3>
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-white" />
                </div>
              </div>
              <p className="text-2xl lg:text-3xl font-bold">
                {formatCurrency(phasedAnalytics?.summary?.thirtyDayRevenue?.realized ?? header.totalRevenue)}
              </p>
              {phasedAnalytics?.summary?.thirtyDayRevenue && (
                <div className="mt-2 space-y-0.5 text-xs text-yellow-100">
                  <div className="flex justify-between">
                    <span>Booked</span>
                    <span>{formatCurrency(phasedAnalytics.summary.thirtyDayRevenue.booked)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Pipeline</span>
                    <span>{formatCurrency(phasedAnalytics.summary.thirtyDayRevenue.pipeline)}</span>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          [...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse bg-gray-100 h-32 rounded-2xl" />
          ))
        )}
      </div>

      {phasedAnalytics && (
        <PhasedMetricsPanel
          title="Phased Economics View"
          subtitle="Realized, booked, and pipeline layers for system economics."
          metrics={phasedMetricConfigs}
          summary={phasedAnalytics.summary}
          variant="icon-cards"
          gridClassName="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6"
        />
      )}

      {/* Charts and Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Daily Orders */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Daily Orders (30d)</h3>
            <span className="text-xs text-gray-500">Patient orders by status</span>
          </div>
          {dailyOrders.length > 0 && dailyOrders.some(d => d.total > 0) ? (
            <BarChart
              data={dailyOrders.map(d => ({
                date: d.date,
                submitted: d.byStatus.submitted || 0,
                processing: d.byStatus.processing || 0,
                shipped: d.byStatus.shipped || 0,
                delivered: d.byStatus.delivered || 0,
                cancelled: (d.byStatus.cancelled || 0) + (d.byStatus.voided || 0) + (d.byStatus.failed || 0)
              }))}
              xKey="date"
              bars={[
                { dataKey: 'submitted', name: 'Submitted', color: '#3b82f6' },
                { dataKey: 'processing', name: 'Processing', color: '#f59e0b' },
                { dataKey: 'shipped', name: 'Shipped', color: '#8b5cf6' },
                { dataKey: 'delivered', name: 'Delivered', color: '#10b981' },
                { dataKey: 'cancelled', name: 'Cancelled', color: '#ef4444' }
              ]}
              height={320}
              layout="horizontal"
              stacked
              formatXAxisLabel={formatIntervalLabel('daily')}
            />
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No order activity in the last 30 days</p>
              </div>
            </div>
          )}
        </div>

        {/* Revenue Trends — trends slice */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Revenue Trends</h3>
            <TrendIntervalSelector
              selectedInterval={revenueInterval}
              onIntervalChange={setRevenueInterval}
            />
          </div>
          {!trends ? (
            <div className="animate-pulse bg-gray-100 h-64 rounded-xl" />
          ) : revenueData.length > 0 ? (
            revenueInterval === 'monthly' ? (
              <BarChart
                data={revenueData}
                xKey="month"
                bars={[
                  { dataKey: "revenue", name: "Revenue", color: "#22c55e" },
                  { dataKey: "expenses", name: "Expenses", color: "#ef4444" }
                ]}
                height={250}
                layout="horizontal"
                formatTooltip={(value: any, name: string) => {
                  if (name === "Revenue" || name === "Expenses") return [formatCurrency(value), name];
                  return [value.toLocaleString(), name];
                }}
                formatXAxisLabel={formatIntervalLabel(revenueInterval)}
              />
            ) : (
              <LineChart
                data={revenueData}
                xKey="month"
                lines={[
                  {
                    dataKey: "revenue",
                    name: "Revenue",
                    color: "#22c55e",
                    strokeWidth: 3
                  },
                  {
                    dataKey: "expenses",
                    name: "Expenses",
                    color: "#ef4444",
                    strokeWidth: 2
                  }
                ]}
                height={250}
                formatTooltip={(value: any, name: string) => {
                  if (name === "Revenue" || name === "Expenses") return [formatCurrency(value), name];
                  return [value.toLocaleString(), name];
                }}
                formatXAxisLabel={formatIntervalLabel(revenueInterval)}
              />
            )
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <DollarSign className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No revenue data available</p>
              </div>
            </div>
          )}
        </div>

        {/* Top Account Managers Performance — rankings slice */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Top Account Managers</h3>
          {!rankings ? (
            <div className="animate-pulse bg-gray-100 h-64 rounded-xl" />
          ) : topAccountManagersData.length > 0 ? (
            <BarChart
              data={topAccountManagersData}
              xKey="name"
              bars={[
                {
                  dataKey: "revenue",
                  name: "Revenue",
                  color: "#8b5cf6"
                }
              ]}
              height={250}
              formatTooltip={(value: any, name: string) => {
                if (name === "Revenue") return [formatCurrency(value), name];
                return [value.toLocaleString(), name];
              }}
            />
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No account manager data available</p>
              </div>
            </div>
          )}
        </div>

        {/* Top Physicians Performance — rankings slice */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Top Physicians</h3>
          {!rankings ? (
            <div className="animate-pulse bg-gray-100 h-64 rounded-xl" />
          ) : topPhysiciansData.length > 0 ? (
            <BarChart
              data={topPhysiciansData}
              xKey="name"
              bars={[
                {
                  dataKey: "revenue",
                  name: "Revenue",
                  color: "#10b981"
                }
              ]}
              height={250}
              formatTooltip={(value: any, name: string) => {
                if (name === "Revenue") return [formatCurrency(value), name];
                return [value.toLocaleString(), name];
              }}
            />
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <UserCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No physician data available</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Operations: User growth + Fulfillment snapshot */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* User Growth Trends — trends slice */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">User Growth Trends</h3>
            <TrendIntervalSelector
              selectedInterval={userGrowthInterval}
              onIntervalChange={setUserGrowthInterval}
            />
          </div>
          {!trends ? (
            <div className="animate-pulse bg-gray-100 h-64 rounded-xl" />
          ) : userGrowthData.length > 0 ? (
            userGrowthInterval === 'monthly' ? (
              <BarChart
                data={userGrowthData}
                xKey="month"
                bars={[
                  { dataKey: "users", name: "Total Users", color: "#3b82f6" },
                  { dataKey: "physicians", name: "Physicians", color: "#10b981" },
                  { dataKey: "distributors", name: "Distributors", color: "#8b5cf6" }
                ]}
                height={250}
                layout="horizontal"
                formatTooltip={(value: any, name: string) => [value.toLocaleString(), name]}
                formatXAxisLabel={formatIntervalLabel(userGrowthInterval)}
              />
            ) : (
              <LineChart
                data={userGrowthData}
                xKey="month"
                lines={[
                  {
                    dataKey: "users",
                    name: "Total Users",
                    color: "#3b82f6",
                    strokeWidth: 3
                  },
                  {
                    dataKey: "physicians",
                    name: "Physicians",
                    color: "#10b981",
                    strokeWidth: 2
                  },
                  {
                    dataKey: "distributors",
                    name: "Distributors",
                    color: "#8b5cf6",
                    strokeWidth: 2
                  }
                ]}
                height={250}
                formatTooltip={(value: any, name: string) => [value.toLocaleString(), name]}
                formatXAxisLabel={formatIntervalLabel(userGrowthInterval)}
              />
            )
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No growth data available</p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Fulfillment Status</h3>
            <button
              onClick={() => navigate('/admin/fulfillment')}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Open queue →
            </button>
          </div>
          {fulfillment ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {(['submitted', 'processing', 'shipped'] as const).map((status) => {
                  const a = fulfillment.aging[status];
                  return (
                    <div key={status} className="rounded-xl border border-gray-200 p-3">
                      <div className="text-xs uppercase tracking-wide text-gray-500 capitalize">{status}</div>
                      <div className="text-2xl font-semibold text-gray-900 mt-1">{a?.count ?? 0}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {a ? `avg ${a.avgDaysInStatus}d · oldest ${a.oldestDays}d` : 'no open orders'}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-700">Stuck orders ({fulfillment.stuck.length})</h4>
                  <span className="text-xs text-gray-500">≥5 days in current status</span>
                </div>
                {fulfillment.stuck.length === 0 ? (
                  <div className="text-sm text-gray-500 italic py-4 text-center">
                    Nothing stuck. Nice.
                  </div>
                ) : (
                  <div className="max-h-60 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-xl">
                    {fulfillment.stuck.slice(0, 10).map((s) => (
                      <button
                        key={s.orderId}
                        onClick={() => navigate(`/admin/fulfillment?orderId=${s.orderId}`)}
                        className="w-full px-3 py-2 flex justify-between items-center hover:bg-gray-50 text-left"
                      >
                        <div>
                          <div className="text-sm font-medium text-gray-900">{s.orderNumber}</div>
                          <div className="text-xs text-gray-500">
                            {s.physicianName} · <span className="capitalize">{s.status}</span>
                          </div>
                        </div>
                        <div className="text-xs text-amber-600 font-semibold">
                          {s.daysInStatus}d
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No fulfillment data</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* System Overview — also driven by the header slice. */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* System Metrics */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">System Metrics</h3>
        {header ? (
        <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="font-medium text-gray-700">Active Users</span>
                </div>
              <span className="font-semibold text-gray-900">{header.activeUsers.toLocaleString()}</span>
                </div>
            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="font-medium text-gray-700">Pending Users</span>
              </div>
              <span className="font-semibold text-gray-900">{header.pendingUsers.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="font-medium text-gray-700">Total Patients</span>
              </div>
              <span className="font-semibold text-gray-900">{header.totalPatients.toLocaleString()}</span>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  <span className="font-medium text-gray-700">Total Orders</span>
                </div>
                <span className="font-semibold text-gray-900">{header.totalOrders.toLocaleString()}</span>
              </div>
              {header.orderBreakdown && (
                <div className="mt-2 pl-6 space-y-1 text-xs text-gray-500">
                  <div className="flex justify-between">
                    <span>Patient Orders</span>
                    <span>{header.orderBreakdown.patient.total.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Bulk Orders</span>
                    <span>{header.orderBreakdown.bulk.total.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="font-medium text-gray-700">Avg Order Value</span>
              </div>
              <span className="font-semibold text-gray-900">{formatCurrency(header.averageOrderValue)}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>
                <span className="font-medium text-gray-700">Monthly System Revenue</span>
              </div>
              <span className="font-semibold text-gray-900">{formatCurrency(header.monthlyRevenue)}</span>
            </div>
        </div>
        ) : (
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse bg-gray-100 h-12 rounded-xl" />
            ))}
          </div>
        )}
      </div>

        {/* Pending Approvals */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Pending Approvals</h3>
            <div className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
              {header ? `${header.pendingUsers} pending` : '— pending'}
            </div>
          </div>
          {!header ? (
            <div className="animate-pulse bg-gray-100 h-40 rounded-xl" />
          ) : header.pendingUsers > 0 ? (
            <div className="space-y-4">
              <div className="border border-yellow-200 bg-yellow-50 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <UserCheck className="h-4 w-4 text-yellow-600" />
                      <h4 className="font-medium text-gray-900">Users Awaiting Approval</h4>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{header.pendingUsers} users need administrator review</p>
                    <p className="text-xs text-gray-500 mt-1">Review pending applications in User Management</p>
                  </div>
                </div>
                <div className="flex space-x-2 mt-3">
                  <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-3 rounded-lg transition-colors">
                    Review Applications
          </button>
        </div>
      </div>
    </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">All Caught Up!</h4>
              <p className="text-gray-600">No pending user applications at this time.</p>
            </div>
          )}
        </div>
      </div>

      {/* Clinic Opportunities by AM */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-4 lg:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              <h3 className="text-base lg:text-lg font-semibold text-gray-900">Clinic Opportunities by AM</h3>
            </div>
            <button
              onClick={() => navigate('/analytics?tab=clinic-opportunities')}
              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              View All →
            </button>
          </div>
        </div>
        <div className="p-4 lg:p-6">
          {!rankings ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse bg-gray-100 h-14 rounded-lg" />
              ))}
            </div>
          ) : rankings.topDistributors.length > 0 ? (
            <div className="space-y-2">
              {rankings.topDistributors.slice(0, 5).map((distributor) => (
                <div
                  key={distributor.distributorId}
                  onClick={() => navigate(`/analytics?tab=clinic-opportunities&distributorId=${distributor.distributorId}`)}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-purple-50 cursor-pointer transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-900">{distributor.distributorName}</span>
                      <div className="text-xs text-gray-500">
                        {distributor.physicianCount} clinics • {distributor.orderCount} orders
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-900">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      }).format(distributor.revenue)}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-purple-500 transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Building2 className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>No Account Managers found</p>
            </div>
          )}
        </div>
      </div>

      {/* Revenue Breakdown Modal */}
      <RevenueBreakdownModal
        isOpen={revenueModalOpen}
        onClose={() => setRevenueModalOpen(false)}
        revenueType={selectedRevenueType}
        title={selectedRevenueTitle}
        dateRange={getDateRange()}
      />
    </div>
);
};

export const Dashboard: React.FC = () => {
  const user = useUser();
  const { branding } = useTenantBranding();

  if (!user) {
    return null;
  }

  const renderDashboard = () => {
    switch (user.role) {
      case 'admin':
        return <AdminDashboard />;
      case 'distributor':
        return <DistributorDashboard />;
      case 'supervisor':
        return <DistributorDashboard />;
      case 'physician':
      case 'physician_readonly':
        return <PhysicianDashboard />;
      // A payout-only 'partner' has no dashboard of their own — their entire
      // surface is the payouts page, so land them there instead of on the
      // "your dashboard is being prepared" placeholder below.
      case 'partner':
        return <Navigate to="/payouts" replace />;
      default:
        return (
          <div className="text-center py-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Welcome to {branding.companyName}
            </h2>
            <p className="text-gray-600">
              Your dashboard is being prepared based on your role.
            </p>
          </div>
        );
    }
  };

  return (
    <div>      
      {renderDashboard()}
    </div>
  );
}; 
